import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDynamicTableHeight } from "@/hooks/use-dynamic-table-height";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollableTabsContainer } from "@/components/ui/scrollable-tabs-container";
import { StickyFooter } from "@/components/ui/sticky-footer";
import { ClientCombobox } from "@/components/ui/client-combobox";
import { EmployeeCombobox } from "@/components/ui/employee-combobox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Calendar,
  Coins,
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Activity,
  BarChart3,
  Edit,
  MoreVertical,
  UserCheck,
  UserX,
  ArrowUpDown,
  Settings2,
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Trash2,
  LayoutDashboard,
  Landmark,
  PieChart,
  Building2,
  Layers,
  UsersRound,
  Milestone as MilestoneIcon,
  Banknote,
  GitPullRequestArrow,
  ArrowLeft,
  History,
  Download,
  ShoppingCart,
  Receipt,
  CreditCard,
  Loader2,
  Columns,
  Search,
  Pencil,
  X,
  ShieldCheck,
} from "lucide-react";
import { SensitiveData } from "@/components/sensitive-data";
import {
  BarChart as RechartsBarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell as RechartsCell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  BudgetCard,
  SalesBudgetView,
  CostBudgetView,
  ResourceBudgetWrapper,
  BudgetSummaryCards,
  BudgetAlertsSection,
  EditBudgetModal,
  BudgetHistoryModal,
  BudgetExportModal,
} from "@/components/blocks/budget";
import { EditResourceBudgetPage } from "@/components/blocks/budget/edit-resource-budget-page";
import { EditCostBudgetPage } from "@/components/blocks/budget/edit-cost-budget-page";
import { EditSalesBudgetPage } from "@/components/blocks/budget/edit-sales-budget-page";
import { ResourceBudgetMetrics } from "@/components/blocks/budget/resource-budget-metrics";
import { UnfulfilledRolesTable } from "@/components/blocks/unfulfilled-roles-table";
import { InvoiceGenerationWizard } from "@/components/billing/InvoiceGenerationWizard";
import { InvoiceDetailsSheet } from "@/components/billing/InvoiceDetailsSheet";
import { FixedFeeBillingTab } from "@/components/billing/FixedFeeBillingTab";
import { ProjectAuditTab } from "@/components/blocks/audit/project-audit-tab";
import type { AuditInvoice } from "@/components/blocks/audit/project-audit-tab";
import { useInvoiceTemplates } from "@/contexts/InvoiceTemplateContext";
import { getProjectBillingConfig, updateProjectBillingConfig } from "@/services/billingService";
import { CreateDraftDialog } from "@/components/billing/CreateDraftDialog";
import { useBudgetPermissions } from "@/hooks/use-budget-permissions";
import { useBudgetData, useUpdateBudget } from "@/hooks/use-budget-data";
import { useResourceBudgetSettings } from "@/hooks/use-resource-budget-settings";
import { enrichBudgetWithAllocatedHours } from "@/lib/budget-team-utils";
import { useCompany } from "@/contexts/CompanyContext";
import type { BudgetData } from "@/types";
import PageHeaderBlock from "@/registry/evo-shadcn/blocks/page-header";
import { toast } from "sonner";
import {
  CostCentresTable,
  DEFAULT_COST_CENTRE_COLUMNS,
  type CostCentre,
  type CostCentreColumnDefinition,
} from "@/components/blocks/cost-centres/cost-centres-table";
import { CostCentresColumnConfigurator } from "@/components/blocks/cost-centres/cost-centres-column-configurator";
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import {
  CostCentresHierarchicalTable,
  type HierarchicalCostCentre,
} from "@/components/blocks/cost-centres/cost-centres-hierarchical-table";
import { PurchasesCommitmentsTab } from "@/components/blocks/purchases/purchases-commitments-tab";
import { TimeEntriesTab } from "@/components/blocks/time/time-entries-tab";
import { ExpenseEntriesTab } from "@/components/blocks/expenses/expense-entries-tab";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfigureStructureModal } from "@/components/blocks/cost-centres/configure-structure-modal";
import { CostCentreDetailsPanel } from "@/components/blocks/cost-centres/cost-centre-details-panel";
import { CreateCostCentrePanel } from "@/components/blocks/cost-centres/create-cost-centre-panel";
import { CostCentreFullModal } from "@/components/blocks/cost-centres/cost-centre-full-modal";
import { formatHours } from "@/lib/budget-utils";
import {
  fetchProjectById,
  fetchClients,
  fetchEmployees,
  fetchResourceAllocations,
  fetchCostCentresByProjectId,
  fetchTimeEntriesByProjectId,
  fetchExpensesByProjectId,
  fetchMilestonesByProjectId,
  fetchChangeRequestsByProjectId,
  createCostCentre,
  updateProject,
  getDataMode,
  createResourceAllocation,
  deleteResourceAllocation,
  createPersonBudget,
  saveColumnPreferences,
  fetchColumnPreferences,
  fetchResourceBudgetIdForProject,
  fetchBillingWipItems,
  fetchBillingWipItemsPaged,
  fetchBillingInvoices,
  createBillingDraft,
  updateBillingDraft,
  deleteBillingDraft,
  postBillingDraftToFinancials,
  voidBillingInvoice,
  mapInvoiceStatus,
  mapWipSourceType,
} from "@/lib/data-service";
import type { FetchExpensesOptions } from "@/lib/data-service";
import type { BillingWipItem, BillingInvoice, PostToFinancialsResponse, PaginatedBillingWipResponse, WipSummary } from "@/lib/data-service";
import { useErrorHandler } from "@/hooks/use-error-handler";
import {
  formatCurrency,
  formatPercent,
  formatDate,
  formatDateForInput,
  getHealthBadgeColor,
  getStatusColor,
  formatStatus,
  getVarianceColor,
  cn,
} from "@/lib/utils";
import { ROUTES } from "@/routes";
import type {
  Project,
  Client,
  Employee,
  TimeEntry,
  Expense,
  Milestone,
  ResourceAllocation,
  ChangeRequest,
  CompletionPercentAuditEntry,
} from "@/types";
import { PROJECT_STATUS_LABELS } from "@/types";
import { mockCompletionAuditData } from "@/lib/mock-data";
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';

interface ProjectDetailsPageProps {
  projectId?: string;
  onBack?: () => void;
}

interface TeamMember {
  id: number | string;
  employeeId: number;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  allocatedHours: number;
  actualHours: number;
  remaining: number;
  startDate: string;
  endDate: string;
  status: "On Track" | "At Risk" | "Over Allocated";
  utilisationPercent: number;
}

// Default invoice column definitions (extracted for useColumnPreferences)
const DEFAULT_INVOICE_COLUMNS: ColumnDefinition[] = [
  {
    id: 'invoice-number',
    name: 'Invoice #',
    description: 'Invoice number',
    category: 'Status',
    visible: true,
    locked: true,
    order: 0,
  },
  {
    id: 'date',
    name: 'Date',
    description: 'Invoice date',
    category: 'Timeline',
    visible: true,
    order: 1,
  },
  {
    id: 'description',
    name: 'Description',
    description: 'Invoice description',
    category: 'Status',
    visible: true,
    order: 2,
  },
  {
    id: 'amount',
    name: 'Amount',
    description: 'Invoice amount',
    category: 'Financial',
    visible: true,
    order: 3,
    align: 'right' as const,
  },
  {
    id: 'status',
    name: 'Status',
    description: 'Invoice status',
    category: 'Status',
    visible: true,
    order: 4,
  },
];

// Default WIP column definitions (extracted for useColumnPreferences)
const DEFAULT_WIP_COLUMNS: ColumnDefinition[] = [
  {
    id: 'date',
    name: 'Date',
    description: 'Work date',
    category: 'Timeline',
    visible: true,
    locked: true,
    order: 0,
  },
  {
    id: 'resource',
    name: 'Resource',
    description: 'Person or resource',
    category: 'Resource',
    visible: true,
    order: 1,
  },
  {
    id: 'task',
    name: 'Task',
    description: 'Task name',
    category: 'Status',
    visible: true,
    order: 2,
  },
  {
    id: 'description',
    name: 'Description',
    description: 'Work description',
    category: 'Status',
    visible: true,
    order: 3,
  },
  {
    id: 'type',
    name: 'Type',
    description: 'Time or Expense',
    category: 'Status',
    visible: true,
    order: 4,
  },
  {
    id: 'quantity',
    name: 'Qty',
    description: 'Quantity (hours or items)',
    category: 'Financial',
    visible: true,
    order: 5,
    align: 'right' as const,
  },
  {
    id: 'rate',
    name: 'Rate',
    description: 'Billing rate',
    category: 'Financial',
    visible: true,
    order: 6,
    align: 'right' as const,
  },
  {
    id: 'amount',
    name: 'Amount',
    description: 'Total amount',
    category: 'Financial',
    visible: true,
    order: 7,
    align: 'right' as const,
  },
  {
    id: 'costCentre',
    name: 'Cost Centre',
    description: 'External cost centre code',
    category: 'Financial',
    visible: true,
    order: 8,
  },
];

export function ProjectDetailsPage({ projectId: projectIdProp, onBack }: ProjectDetailsPageProps) {
  const { statusLabels, selectableStatuses } = useProjectStatuses();
  // Get projectId from URL params or props (props for backward compatibility)
  const { projectId: projectIdFromUrl } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = projectIdProp || projectIdFromUrl || "";
  const numericProjectId = projectId ? parseInt(projectId, 10) : 0;

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<Project | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [recentClientIds, setRecentClientIds] = useState<string[]>([]);
  const [projectManager, setProjectManager] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [recentProjectManagerIds, setRecentProjectManagerIds] = useState<string[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [resourceAllocations, setResourceAllocations] = useState<ResourceAllocation[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [localBudgetData, setLocalBudgetData] = useState<BudgetData | null>(null);
  const [isStructureView, setIsStructureView] = useState(false);
  const [sortField, setSortField] = useState<keyof TeamMember>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();
  const { templates: invoiceTemplates, getTemplate: getInvoiceTemplate } = useInvoiceTemplates();

  // Budget permissions hook
  const budgetPermissions = useBudgetPermissions();
  const { isBudgetingEnabled } = budgetPermissions;

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Project>>({});

  // Active tab state
  const [activeTab, setActiveTab] = useState("overview");

  // WIP Items state (for Unbilled Work tab)
  const [selectedWipItems, setSelectedWipItems] = useState<string[]>([]);
  const [wipSearchQuery, setWipSearchQuery] = useState("");
  const [wipTypeFilter, setWipTypeFilter] = useState("all-types");
  const [wipResourceFilter, setWipResourceFilter] = useState("all-resources");
  const [wipGroupBy, setWipGroupBy] = useState("none");
  const [wipDateFrom, setWipDateFrom] = useState<Date | undefined>(undefined);
  const [wipDateTo, setWipDateTo] = useState<Date | undefined>(undefined);
  const [wipCurrentPage, setWipCurrentPage] = useState(1);
  const [wipItemsPerPage, setWipItemsPerPage] = useState(25);
  const [wipSortColumn, setWipSortColumn] = useState<string>("date");
  const [wipSortDirection, setWipSortDirection] = useState<"asc" | "desc">("desc");

  // Billing data state (live mode)
  const [billingWipItems, setBillingWipItems] = useState<BillingWipItem[]>([]);
  const [billingInvoices, setBillingInvoices] = useState<BillingInvoice[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingActionLoading, setBillingActionLoading] = useState(false);
  const [wipTotalCount, setWipTotalCount] = useState(0);
  const [wipSummary, setWipSummary] = useState<WipSummary>({ totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 });

  // Collapsible KPI cards state (expanded by default)
  const [isKpiOpen, setIsKpiOpen] = useState(true);

  // Expense type checkboxes state - driven by company integration settings
  const [includeApprovedExpenses, setIncludeApprovedExpenses] = useState(
    currentCompany?.approvedExpensesEnabled ?? true,
  );
  const [includeCreditCardExpenses, setIncludeCreditCardExpenses] = useState(
    currentCompany?.creditCardExpensesEnabled ?? false,
  );
  const [expensesLoading, setExpensesLoading] = useState(false);

  // Cost Centres state
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  const [hierarchicalCostCentres, setHierarchicalCostCentres] = useState<HierarchicalCostCentre[]>([]);
  const [costCentreColumns, setCostCentreColumns] = useState<CostCentreColumnDefinition[]>(() => {
    // Initialize from localStorage as immediate fallback
    try {
      const stored = localStorage.getItem('cost-centre-column-preferences');
      if (stored) {
        return JSON.parse(stored) as CostCentreColumnDefinition[];
      }
    } catch {
      // Ignore parse errors
    }
    return DEFAULT_COST_CENTRE_COLUMNS;
  });
  const [costCentreColumnConfigOpen, setCostCentreColumnConfigOpen] = useState(false);

  // Load cost centre column preferences from API in live mode
  useEffect(() => {
    async function loadCostCentreColumnPrefs() {
      const mode = getDataMode();
      if (mode !== 'live') return;
      try {
        const apiColumns = await fetchColumnPreferences('cost-centre-column-preferences');
        if (apiColumns && apiColumns.length > 0) {
          setCostCentreColumns(apiColumns as unknown as CostCentreColumnDefinition[]);
        }
      } catch (error) {
        console.error('Failed to load cost centre column preferences from API:', error);
      }
    }
    loadCostCentreColumnPrefs();
  }, []);

  // Project Settings state
  const [projectSettings, setProjectSettings] = useState({
    billingFrequency: "monthly",
    invoiceTemplateId: null as string | number | null,
    expenseRecharge: "at-cost",
    expenseMarkupPercent: "",
    purchaseRecharge: "at-cost",
    purchaseMarkupPercent: "",
    notToExceedCapEnabled: false,
    notToExceedCapAmount: "",
    // Auto-generate invoice scheduling
    autoGenerateEnabled: false,
    monthlyPattern: "specific-day",
    monthlyDay: "1",
    weeklyDay: "friday",
  });

  // Load persisted billing config (including invoiceTemplateId) from localStorage on mount.
  // Falls back gracefully if nothing has been saved yet, using the default template.
  useEffect(() => {
    if (!numericProjectId) return;
    getProjectBillingConfig(numericProjectId).then((config) => {
      const storedId = config.invoiceTemplateId;
      if (storedId !== null && storedId !== undefined) {
        setProjectSettings((prev) => ({ ...prev, invoiceTemplateId: storedId }));
      } else {
        // No template saved yet — resolve and persist the default template id
        const defaultTpl = invoiceTemplates.find((t) => t.isDefault) ?? invoiceTemplates[0];
        if (defaultTpl) {
          setProjectSettings((prev) => ({ ...prev, invoiceTemplateId: defaultTpl.id }));
          updateProjectBillingConfig(numericProjectId, { invoiceTemplateId: defaultTpl.id });
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericProjectId]);

  const [settingsSaving, setSettingsSaving] = useState(false);
  const [configureStructureOpen, setConfigureStructureOpen] = useState(false);
  const [selectedCostCentre, setSelectedCostCentre] = useState<CostCentre | null>(null);
  const [costCentreDetailsPanelOpen, setCostCentreDetailsPanelOpen] = useState(false);
  const [costCentreFullModalOpen, setCostCentreFullModalOpen] = useState(false);
  const [createCostCentrePanelOpen, setCreateCostCentrePanelOpen] = useState(false);

  // Budget state
  const {
    data: budgetData,
    loading: budgetLoading,
    error: budgetError,
    refetch: refetchBudget,
  } = useBudgetData(numericProjectId);
  const { updateBudget, loading: updateBudgetLoading } = useUpdateBudget();
  const { settings: resourceSettings } = useResourceBudgetSettings();
  const [salesBudgetEditModalOpen, setSalesBudgetEditModalOpen] = useState(false);
  const [editSalesBudgetPageOpen, setEditSalesBudgetPageOpen] = useState(false);
  const [costBudgetEditModalOpen, setCostBudgetEditModalOpen] = useState(false);
  const [editCostBudgetPageOpen, setEditCostBudgetPageOpen] = useState(false);
  const [billingSettingsDrawerOpen, setBillingSettingsDrawerOpen] = useState(false);
  const [drawerEditMode, setDrawerEditMode] = useState(false);
  const [drawerSettings, setDrawerSettings] = useState(projectSettings);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [resourceBudgetEditModalOpen, setResourceBudgetEditModalOpen] = useState(false);
  const [editResourceBudgetPageOpen, setEditResourceBudgetPageOpen] = useState(false);
  const [budgetHistoryModalOpen, setBudgetHistoryModalOpen] = useState(false);
  const [budgetExportModalOpen, setBudgetExportModalOpen] = useState(false);
  const [activeBudgetType, setActiveBudgetType] = useState<"sales" | "cost" | "resource">("sales");
  const [invoiceWizardOpen, setInvoiceWizardOpen] = useState(false);
  // Fixed Fee billing — tracks which milestone opened the wizard (Phase 3/4)
  const [fixedFeeMilestone, setFixedFeeMilestone] = useState<{ id: number; name: string; amount: number; dueDate: string } | null>(null);

  // Completion % override editing (Phase 13.2)
  const [completionPercentEditing, setCompletionPercentEditing] = useState(false);
  const [completionPercentInputValue, setCompletionPercentInputValue] = useState<string>('');
  // Audit trail collapsible (Phase 13.4)
  const [completionAuditOpen, setCompletionAuditOpen] = useState(false);
  // Audit tab — pre-set category filter when navigating from the KPI card link
  const [auditCategoryFilter, setAuditCategoryFilter] = useState<string>("all");
  // Completion % audit data sourced from mock data keyed by project ID
  const completionAuditEntries: CompletionPercentAuditEntry[] = project ? (mockCompletionAuditData[project.id] ?? []) : [];

  // Invoice details sheet state
  const [invoiceDetailsSheetOpen, setInvoiceDetailsSheetOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);

  // Create draft dialog state
  const [createDraftDialogOpen, setCreateDraftDialogOpen] = useState(false);
  
  // Invoice column configuration
  const [invoiceColumnConfigOpen, setInvoiceColumnConfigOpen] = useState(false);
  
  // Invoice pagination state
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(1);
  const [invoiceItemsPerPage, setInvoiceItemsPerPage] = useState(25);
  
  // Invoice filtering and search state
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>("all");
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState("");

  // Invoice columns - persisted per user via useColumnPreferences
  const { columns: invoiceColumns, updateColumns: updateInvoiceColumns } = useColumnPreferences({
    storageKey: 'billing-invoice-column-preferences',
    defaultColumns: DEFAULT_INVOICE_COLUMNS,
  });

  // Get visible invoice columns
  const visibleInvoiceColumns = useMemo(
    () => invoiceColumns.filter(col => col.visible).sort((a, b) => a.order - b.order),
    [invoiceColumns]
  );

  // WIP column configuration state
  const [wipColumnConfigOpen, setWipColumnConfigOpen] = useState(false);

  // WIP columns - persisted per user via useColumnPreferences
  const { columns: wipColumns, updateColumns: updateWipColumns } = useColumnPreferences({
    storageKey: 'billing-wip-column-preferences',
    defaultColumns: DEFAULT_WIP_COLUMNS,
  });

  const visibleWipColumns = useMemo(
    () => wipColumns.filter(col => col.visible).sort((a, b) => a.order - b.order),
    [wipColumns]
  );

  // Check if resource budgeting is enabled
  const isResourceBudgetEnabled =
    resourceSettings.budgetType !== "none" && budgetPermissions.settings.permissions.resourceBudgetEnabled;

  // Enrich budget data with allocated hours from team members
  const enrichedBudgetData = useMemo(() => {
    // Use local budget data if available (after assignments), otherwise use fetched data
    const dataToEnrich = localBudgetData || budgetData;
    if (!dataToEnrich || !dataToEnrich.resourceBudget) return dataToEnrich;

    return {
      ...dataToEnrich,
      resourceBudget: enrichBudgetWithAllocatedHours(dataToEnrich.resourceBudget, teamMembers),
    };
  }, [budgetData, localBudgetData, teamMembers]);

  // Debug logging
  console.log("[ProjectDetails] Budget permissions:", {
    isBudgetingEnabled,
    salesEnabled: budgetPermissions.settings.permissions.salesBudgetEnabled,
    costEnabled: budgetPermissions.settings.permissions.costBudgetEnabled,
    resourceEnabled: isResourceBudgetEnabled,
  });

  // Dynamic height for tabs container
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabsContainerHeight = useDynamicTableHeight(tabsContainerRef, 24); // 24px bottom padding

  // WIP Items data - uses API data in live mode, mock data in demo mode
  const dataMode = getDataMode();

  const wipItems = useMemo(() => {
    if (dataMode === "live") {
      // Live mode: transform API data to match existing table shape (may be empty)
      return billingWipItems.map((item) => ({
        id: `wip-${item.id}`,
        date: item.date ? item.date.split("T")[0] : "",
        resource: item.employeeName || (item.sourceType === 1 || item.sourceType === 2 ? "Unknown" : "-"),
        task: item.taskName || "",
        description: item.description || "",
        type: mapWipSourceType(item.sourceType),
        quantity: item.quantity,
        unit: item.unit || (item.sourceType === 1 ? "hrs" : "units"),
        rate: item.rate,
        amount: item.netAmount,
        costCentre: item.externalCostCentreCode || "",
        reference: `WIP-${item.id}`,
        costRate: item.costRate || 0,
        billingId: item.id, // preserve original ID for API calls
      }));
    }

    // Demo mode: use mock data
    return [
      {
        id: "wip-1",
        date: "2024-01-15",
        resource: "John Smith",
        task: "Backend Development",
        description: "API endpoint implementation",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 150,
        amount: 1200,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-2",
        date: "2024-01-16",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Dashboard mockups and prototypes",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 125,
        amount: 812.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-3",
        date: "2024-01-17",
        resource: "Mike Wilson",
        task: "Testing",
        description: "QA and bug fixes",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 100,
        amount: 500,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-4",
        date: "2024-01-18",
        resource: "John Smith",
        task: "Travel",
        description: "Client meeting - London",
        type: "Expense",
        quantity: 1,
        unit: "trip",
        rate: 245.5,
        amount: 245.5,
        costCentre: "Travel",
        reference: "EXP-123",
        costRate: 245.5,
      },
      {
        id: "wip-5",
        date: "2024-01-19",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Component library setup",
        type: "Time",
        quantity: 7.5,
        unit: "hrs",
        rate: 125,
        amount: 937.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-6",
        date: "2024-01-20",
        resource: "Mike Wilson",
        task: "Software",
        description: "Development tools license",
        type: "Expense",
        quantity: 1,
        unit: "item",
        rate: 299,
        amount: 299,
        costCentre: "IT",
        reference: "EXP-124",
        costRate: 299,
      },
      {
        id: "wip-7",
        date: "2024-01-21",
        resource: "Procurement",
        task: "Hardware",
        description: "Dell Laptop - Developer workstation",
        type: "Purchase",
        quantity: 2,
        unit: "units",
        rate: 1500,
        amount: 3000,
        costCentre: "IT",
        reference: "PO-001",
        costRate: 1200,
      },
      {
        id: "wip-8",
        date: "2024-01-22",
        resource: "Procurement",
        task: "Office Supplies",
        description: "Ergonomic chairs for team",
        type: "Purchase",
        quantity: 5,
        unit: "units",
        rate: 450,
        amount: 2250,
        costCentre: "Office",
        reference: "PO-002",
        costRate: 350,
      },
      {
        id: "wip-9",
        date: "2024-01-23",
        resource: "John Smith",
        task: "Backend Development",
        description: "Database optimization",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 150,
        amount: 900,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-10",
        date: "2024-01-24",
        resource: "Procurement",
        task: "Software Licenses",
        description: "Adobe Creative Cloud - Annual subscription",
        type: "Purchase",
        quantity: 3,
        unit: "licenses",
        rate: 599,
        amount: 1797,
        costCentre: "Design",
        reference: "PO-003",
        costRate: 599,
      },
      {
        id: "wip-11",
        date: "2024-01-25",
        resource: "John Smith",
        task: "Backend Development",
        description: "Authentication module implementation",
        type: "Time",
        quantity: 7.5,
        unit: "hrs",
        rate: 150,
        amount: 1125,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-12",
        date: "2024-01-26",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "User flow diagrams",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 125,
        amount: 625,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-13",
        date: "2024-01-27",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Integration testing",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-14",
        date: "2024-01-28",
        resource: "John Smith",
        task: "Backend Development",
        description: "Payment gateway integration",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 150,
        amount: 975,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-15",
        date: "2024-01-29",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Responsive design implementation",
        type: "Time",
        quantity: 7.0,
        unit: "hrs",
        rate: 125,
        amount: 875,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-16",
        date: "2024-01-30",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Performance testing",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 100,
        amount: 600,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-17",
        date: "2024-01-31",
        resource: "John Smith",
        task: "Backend Development",
        description: "Email notification system",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 150,
        amount: 825,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-18",
        date: "2024-02-01",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Icon design and asset creation",
        type: "Time",
        quantity: 4.5,
        unit: "hrs",
        rate: 125,
        amount: 562.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-19",
        date: "2024-02-02",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Security audit and penetration testing",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-20",
        date: "2024-02-03",
        resource: "John Smith",
        task: "Backend Development",
        description: "Data migration scripts",
        type: "Time",
        quantity: 7.0,
        unit: "hrs",
        rate: 150,
        amount: 1050,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-21",
        date: "2024-02-04",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Animation and transitions",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 125,
        amount: 750,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-22",
        date: "2024-02-05",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Cross-browser compatibility testing",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 100,
        amount: 550,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-23",
        date: "2024-02-06",
        resource: "John Smith",
        task: "Backend Development",
        description: "Caching layer implementation",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 150,
        amount: 975,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-24",
        date: "2024-02-07",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Accessibility improvements",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 125,
        amount: 625,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-25",
        date: "2024-02-08",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Load testing and optimization",
        type: "Time",
        quantity: 7.5,
        unit: "hrs",
        rate: 100,
        amount: 750,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-26",
        date: "2024-02-09",
        resource: "John Smith",
        task: "Travel",
        description: "Client presentation - Manchester",
        type: "Expense",
        quantity: 1,
        unit: "trip",
        rate: 185.75,
        amount: 185.75,
        costCentre: "Travel",
        reference: "EXP-125",
        costRate: 185.75,
      },
      {
        id: "wip-27",
        date: "2024-02-10",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Style guide documentation",
        type: "Time",
        quantity: 4.0,
        unit: "hrs",
        rate: 125,
        amount: 500,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-28",
        date: "2024-02-11",
        resource: "Mike Wilson",
        task: "Testing",
        description: "User acceptance testing coordination",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 100,
        amount: 650,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-29",
        date: "2024-02-12",
        resource: "John Smith",
        task: "Backend Development",
        description: "Logging and monitoring setup",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 150,
        amount: 825,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-30",
        date: "2024-02-13",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Error state designs",
        type: "Time",
        quantity: 3.5,
        unit: "hrs",
        rate: 125,
        amount: 437.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-31",
        date: "2024-02-14",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Regression testing",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-32",
        date: "2024-02-15",
        resource: "John Smith",
        task: "Backend Development",
        description: "API documentation",
        type: "Time",
        quantity: 4.5,
        unit: "hrs",
        rate: 150,
        amount: 675,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-33",
        date: "2024-02-16",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Loading state animations",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 125,
        amount: 687.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-34",
        date: "2024-02-17",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Mobile device testing",
        type: "Time",
        quantity: 7.0,
        unit: "hrs",
        rate: 100,
        amount: 700,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-35",
        date: "2024-02-18",
        resource: "John Smith",
        task: "Backend Development",
        description: "Deployment pipeline setup",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 150,
        amount: 900,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-36",
        date: "2024-02-19",
        resource: "Procurement",
        task: "Cloud Services",
        description: "AWS hosting - Monthly subscription",
        type: "Purchase",
        quantity: 1,
        unit: "month",
        rate: 450,
        amount: 450,
        costCentre: "IT",
        reference: "PO-004",
        costRate: 450,
      },
      {
        id: "wip-37",
        date: "2024-02-20",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Onboarding flow design",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 125,
        amount: 812.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-38",
        date: "2024-02-21",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Final QA review",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-39",
        date: "2024-02-22",
        resource: "John Smith",
        task: "Backend Development",
        description: "Production deployment",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 150,
        amount: 750,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-40",
        date: "2024-02-23",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Post-launch UI refinements",
        type: "Time",
        quantity: 4.5,
        unit: "hrs",
        rate: 125,
        amount: 562.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
    ];
  }, [dataMode, billingWipItems]);

  // Filter and sort WIP items based on search, filters, and date range
  const filteredAndSortedWipItems = useMemo(() => {
    let filtered = wipItems.filter((item) => {
      const matchesSearch =
        wipSearchQuery === "" ||
        item.description.toLowerCase().includes(wipSearchQuery.toLowerCase()) ||
        item.task.toLowerCase().includes(wipSearchQuery.toLowerCase()) ||
        item.resource.toLowerCase().includes(wipSearchQuery.toLowerCase());

      const matchesType = wipTypeFilter === "all-types" || item.type.toLowerCase() === wipTypeFilter.toLowerCase();

      const matchesResource =
        wipResourceFilter === "all-resources" ||
        item.resource.toLowerCase().includes(wipResourceFilter.toLowerCase());

      // Date range filter
      const itemDate = new Date(item.date);
      const matchesDateFrom = !wipDateFrom || itemDate >= wipDateFrom;
      const matchesDateTo = !wipDateTo || itemDate <= wipDateTo;

      return matchesSearch && matchesType && matchesResource && matchesDateFrom && matchesDateTo;
    });

    // Sort items
    filtered.sort((a, b) => {
      let aValue: any = a[wipSortColumn as keyof typeof a];
      let bValue: any = b[wipSortColumn as keyof typeof b];

      // Handle date sorting
      if (wipSortColumn === "date") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle numeric sorting
      if (typeof aValue === "number" && typeof bValue === "number") {
        return wipSortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (wipSortDirection === "asc") {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return bStr < aStr ? -1 : bStr > aStr ? 1 : 0;
      }
    });

    return filtered;
  }, [wipItems, wipSearchQuery, wipTypeFilter, wipResourceFilter, wipDateFrom, wipDateTo, wipSortColumn, wipSortDirection]);

  // Group WIP items if grouping is enabled
  const groupedWipItems = useMemo(() => {
    if (wipGroupBy === "none") {
      return null;
    }

    const groups: Record<string, typeof filteredAndSortedWipItems> = {};

    filteredAndSortedWipItems.forEach((item) => {
      let groupKey = "";
      if (wipGroupBy === "resource") {
        groupKey = item.resource;
      } else if (wipGroupBy === "task") {
        groupKey = item.task;
      } else if (wipGroupBy === "date") {
        groupKey = new Date(item.date).toLocaleDateString("en-GB", { 
          year: "numeric", 
          month: "long", 
          day: "numeric" 
        });
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return groups;
  }, [filteredAndSortedWipItems, wipGroupBy]);

  // Paginate WIP items - in live mode, server already returns one page; in demo mode, slice client-side
  const paginatedWipItems = useMemo(() => {
    if (dataMode === "live") {
      // Server already returns only the current page of items
      return filteredAndSortedWipItems;
    }
    const startIndex = (wipCurrentPage - 1) * wipItemsPerPage;
    const endIndex = startIndex + wipItemsPerPage;
    return filteredAndSortedWipItems.slice(startIndex, endIndex);
  }, [filteredAndSortedWipItems, wipCurrentPage, wipItemsPerPage, dataMode]);

  // Calculate pagination info - use server-side total count in live mode
  const wipTotalItems = dataMode === "live" ? wipTotalCount : filteredAndSortedWipItems.length;
  const wipTotalPages = Math.ceil(wipTotalItems / wipItemsPerPage);
  const wipStartItem = wipTotalItems === 0 ? 0 : (wipCurrentPage - 1) * wipItemsPerPage + 1;
  const wipEndItem = Math.min(wipCurrentPage * wipItemsPerPage, wipTotalItems);

  // Calculate selected totals
  const selectedWipTotals = useMemo(() => {
    const selectedItems = filteredAndSortedWipItems.filter((item) => selectedWipItems.includes(item.id));

    const hours = selectedItems
      .filter((item) => item.type === "Time")
      .reduce((sum, item) => sum + item.quantity, 0);

    const expenseCount = selectedItems.filter((item) => item.type === "Expense").length;

    const amount = selectedItems.reduce((sum, item) => sum + item.amount, 0);

    return {
      count: selectedItems.length,
      hours,
      expenseCount,
      amount,
    };
  }, [filteredAndSortedWipItems, selectedWipItems]);

  // Handle select/deselect individual item
  const handleSelectWipItem = (itemId: string) => {
    setSelectedWipItems((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));
  };

  // Handle select/deselect all filtered items
  const handleSelectAllWipItems = () => {
    // Get all filtered WIP item IDs (across all pages)
    const allWipIds = filteredAndSortedWipItems.map((item) => item.id);
    
    if (selectedWipItems.length === allWipIds.length && allWipIds.length > 0) {
      // If all items are selected, deselect all
      setSelectedWipItems([]);
    } else {
      // Select all items across all pages
      setSelectedWipItems(allWipIds);
    }
  };

  // Handle column sort
  const handleWipSort = (column: string) => {
    if (wipSortColumn === column) {
      setWipSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setWipSortColumn(column);
      setWipSortDirection("asc");
    }
  };

  // Handle page change - fetch new page from server
  const handleWipPageChange = async (newPage: number) => {
    setWipCurrentPage(newPage);
    if (dataMode === "live") {
      try {
        setBillingLoading(true);
        const response = await fetchBillingWipItemsPaged(numericProjectId, 0, newPage, wipItemsPerPage);
        setBillingWipItems(response.items);
        setWipTotalCount(response.totalCount);
        if (response.summary) setWipSummary(response.summary);
      } catch (error) {
        console.error("Failed to fetch WIP page:", error);
      } finally {
        setBillingLoading(false);
      }
    }
  };

  const handleInvoicePageChange = (newPage: number) => {
    setInvoiceCurrentPage(newPage);
  };

  // Helper functions to format display values to match dropdown labels
  const formatProjectType = (type: string): string => {
    const typeMap: Record<string, string> = {
      "fixed-price": "Fixed Price",
      "time-and-materials": "Time & Materials",
      retainer: "Retainer",
    };
    return typeMap[type] || type;
  };

  const formatRevenueRecognition = (method: string): string => {
    const methodMap: Record<string, string> = {
      "percentage-of-completion": "Percentage of Completion",
      "completed-contract": "Completed Contract",
      "cash-basis": "Cash Basis",
    };
    return methodMap[method] || method;
  };

  // Edit mode handlers
  const handleEnterEditMode = () => {
    console.log("Edit mode activated for project:", projectId);
    if (project) {
      setEditFormData({
        name: project.name,
        description: project.description,
        statusId: project.statusId,
        type: project.type,
        revenueRecognitionMethod: project.revenueRecognitionMethod,
        startDate: project.startDate,
        endDate: project.endDate,
        projectManagerId: project.projectManagerId,
      });
      setIsEditMode(true);
      setHasUnsavedChanges(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      setIsEditMode(false);
      setEditFormData({});
    }
  };

  const handleConfirmCancel = () => {
    setIsEditMode(false);
    setEditFormData({});
    setHasUnsavedChanges(false);
    setShowCancelDialog(false);
    toast.info("Changes discarded");
  };

  const handleSaveChanges = async () => {
    try {
      if (!project) return;

      // Validate settings if on Time & Material project
      if (project.type === "time-and-materials" && !validateSettings()) {
        return;
      }

      const dataMode = getDataMode();

      if (dataMode === "live") {
        const updateData = {
          id: numericProjectId,
          name: editFormData.name ?? project.name,
          description: editFormData.description ?? project.description,
          statusId: editFormData.statusId ?? project.statusId,
          type: editFormData.type ?? project.type,
          revenueRecognitionMethod: editFormData.revenueRecognitionMethod ?? project.revenueRecognitionMethod,
          startDate: editFormData.startDate ?? project.startDate,
          endDate: editFormData.endDate ?? project.endDate,
          clientIds: editFormData.clientIds ?? project.clientIds,
          projectManagerId: editFormData.projectManagerId ?? project.projectManagerId,
          health: project.health,
        };

        await updateProject(numericProjectId, updateData);

        // Save project settings for Time & Material projects
        if (project.type === "time-and-materials") {
          // TODO: Call API to save settings when backend is ready
          // await saveProjectSettings(numericProjectId, projectSettings);
        }
      }

      const updatedProject = { ...project, ...editFormData };
      setProject(updatedProject);
      setIsEditMode(false);
      setEditFormData({});
      setHasUnsavedChanges(false);
      toast.success("Project updated successfully");

      window.dispatchEvent(
        new CustomEvent("project-updated", {
          detail: { projectId, project: updatedProject },
        }),
      );
    } catch (error) {
      toast.error("Failed to update project");
      console.error("Error saving project:", error);
    }
  };

  // Budget handlers
  const handleSaveBudget = async (
    updatedBudget: Partial<BudgetData>,
    revisionReason: string,
    saveAsBaseline: boolean,
  ) => {
    console.log("Saving budget:", { updatedBudget, revisionReason, saveAsBaseline });

    try {
      const result = await updateBudget(numericProjectId, updatedBudget);

      if (result.success) {
        console.log("[ProjectDetails] Budget saved successfully, refetching data...");
        toast.success("Budget saved successfully!");
        refetchBudget();
      } else {
        console.error("[ProjectDetails] Failed to save budget:", result.error);
        toast.error(result.error || "Failed to save budget");
      }
    } catch (error) {
      console.error("[ProjectDetails] Error saving budget:", error);
      toast.error("Failed to save budget");
    }

    // Close the appropriate modal
    setSalesBudgetEditModalOpen(false);
    setCostBudgetEditModalOpen(false);
    setResourceBudgetEditModalOpen(false);
  };

  const handleRestoreRevision = async (revisionId: string) => {
    console.log("Restoring revision:", revisionId);
    // TODO: Implement actual restore logic when backend is ready
    toast.success("Revision restored successfully!");
    setBudgetHistoryModalOpen(false);
  };

  const handleFieldChange = (field: keyof Project, value: any) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleProjectManagerChange = (employeeId: string, selectedEmployee: Employee | undefined) => {
    if (selectedEmployee) {
      setProjectManager(selectedEmployee);

      setEditFormData((prev) => ({
        ...prev,
        projectManagerId: selectedEmployee.id,
      }));

      // Update recent project managers list
      const updatedRecentPMs = [employeeId, ...recentProjectManagerIds.filter((id) => id !== employeeId)].slice(0, 10);
      setRecentProjectManagerIds(updatedRecentPMs);
      localStorage.setItem("recentProjectManagerIds", JSON.stringify(updatedRecentPMs));

      setHasUnsavedChanges(true);
    }
  };

  const handleClientChange = (clientId: string, selectedClient: Client | undefined) => {
    if (selectedClient) {
      // Update the client state
      setClient(selectedClient);

      // Update the form data with client ID
      setEditFormData((prev) => ({
        ...prev,
        clientIds: clientId,
      }));

      // Update recent clients list
      const updatedRecentClients = [clientId, ...recentClientIds.filter((id) => id !== clientId)].slice(0, 10); // Keep only the 10 most recent

      setRecentClientIds(updatedRecentClients);
      localStorage.setItem("recentClientIds", JSON.stringify(updatedRecentClients));

      setHasUnsavedChanges(true);
    }
  };

  // Project Settings handlers
  const handleSettingsChange = (field: string, value: any) => {
    setProjectSettings((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  // Drawer-specific handlers
  const handleDrawerSettingsChange = (field: string, value: any) => {
    setDrawerSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleDrawerOpen = (open: boolean) => {
    setBillingSettingsDrawerOpen(open);
    if (open) {
      // Reset drawer state when opening
      setDrawerSettings(projectSettings);
      setDrawerEditMode(false);
    }
  };

  const handleDrawerEdit = () => {
    setDrawerEditMode(true);
  };

  const handleDrawerCancel = () => {
    setDrawerSettings(projectSettings);
    setDrawerEditMode(false);
  };

  const handleDrawerSave = async () => {
    setDrawerSaving(true);
    try {
      // Persist billing config (including invoiceTemplateId) via PUT /api/projects/{id}/billing-config
      if (numericProjectId) {
        await updateProjectBillingConfig(numericProjectId, {
          invoiceTemplateId: drawerSettings.invoiceTemplateId ?? null,
          billingFrequency: drawerSettings.billingFrequency as "weekly" | "fortnightly" | "monthly" | "quarterly" | "on-completion" | "milestone",
          autoGenerate: drawerSettings.autoGenerateEnabled,
        });
      }

      // Update the main project settings
      setProjectSettings(drawerSettings);
      setDrawerEditMode(false);
    } catch (error) {
      console.error("Error saving settings:", error);
    } finally {
      setDrawerSaving(false);
    }
  };

  // ── Completion % override handlers (Phase 13.2 / 13.3) ──────────────────

  // ── Fixed-fee mock invoices — computed once at component level so both the
  // Sales Budget tab (Revenue Health panel) and the Billing Tab share the same
  // data without duplicating the derivation. (Phase 16)
  const fixedFeeInvoices = useMemo(() => {
    const salesBudgetMilestones = enrichedBudgetData?.salesBudget?.milestones ?? [];
    const billingMilestones = salesBudgetMilestones.length > 0
      ? salesBudgetMilestones
      : milestones.map((m) => ({
          id: m.id,
          name: m.name,
          amount: m.paymentAmount ?? 0,
          dueDate: m.dueDate,
        }));

    return [
      {
        id: 101,
        invoiceNumber: 'INV-FF-001' as string | null,
        date: billingMilestones[0]?.dueDate ?? null,
        description: billingMilestones[0]?.name ? `Invoice for: ${billingMilestones[0].name}` : 'Milestone Invoice',
        amount: billingMilestones[0]?.amount ?? 0,
        due: billingMilestones[0]?.dueDate ?? null,
        status: 'Paid' as const,
        milestoneId: billingMilestones[0]?.id,
      },
      {
        id: 102,
        invoiceNumber: 'INV-FF-002' as string | null,
        date: billingMilestones[1]?.dueDate ?? null,
        description: billingMilestones[1]?.name ? `Invoice for: ${billingMilestones[1].name}` : 'Milestone Invoice',
        amount: billingMilestones[1]?.amount ?? 0,
        due: billingMilestones[1]?.dueDate ?? null,
        status: 'Paid' as const,
        milestoneId: billingMilestones[1]?.id,
      },
    ].filter((inv) => inv.amount > 0);
  }, [enrichedBudgetData?.salesBudget?.milestones, milestones]);

  /** Sum of Sent + Paid fixed-fee invoices — used by the Sales Budget Revenue Health panel. */
  const fixedFeeInvoicedToDate = useMemo(
    () => fixedFeeInvoices.filter((i) => (i.status as string) === 'Sent' || (i.status as string) === 'Paid').reduce((s, i) => s + i.amount, 0),
    [fixedFeeInvoices]
  );

  /** Fixed-fee invoices shaped as AuditInvoice[] for the Audit tab (Phase 17).
   *  The fixedFeeInvoices items don't carry createdDate/sentDate/paidDate in the
   *  current mock — we derive them from the invoice date field where possible.
   *  When the backend is live (17-BE.3) this memo is replaced by API data.
   */
  const auditInvoices = useMemo<AuditInvoice[]>(
    () =>
      fixedFeeInvoices.map((inv) => ({
        id: inv.id,
        invoiceNumber: inv.invoiceNumber,
        date: inv.date,
        dueDate: inv.due,
        description: inv.description,
        amount: inv.amount,
        status: inv.status as string,
        milestoneId: inv.milestoneId,
        // Derive event timestamps from status and the invoice date field
        createdDate: inv.date ?? undefined,
        sentDate: (inv.status === 'Paid' || inv.status === 'Sent') && inv.date ? inv.date : undefined,
        paidDate: inv.status === 'Paid' && inv.due ? inv.due : undefined,
      })),
    [fixedFeeInvoices]
  );

  /** The effective completion %, respecting the PM override if set. */
  const effectiveCompletionPercent = project
    ? (project.completionPercentOverride !== undefined
        ? project.completionPercentOverride
        : project.completionPercent)
    : 0;

  /** Auto-calculated % from approved hours ÷ budget hours (mirrors based-on-time logic). */
  const autoCalculatedCompletionPercent = project && project.budgetHours > 0
    ? Math.round((project.actualHours / project.budgetHours) * 100)
    : project?.completionPercent ?? 0;

  const handleCompletionPercentEditStart = () => {
    setCompletionPercentInputValue(String(effectiveCompletionPercent));
    setCompletionPercentEditing(true);
  };

  const handleCompletionPercentEditConfirm = () => {
    if (!project) return;
    const parsed = parseInt(completionPercentInputValue, 10);
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      setCompletionPercentEditing(false);
      return;
    }
    const now = new Date().toISOString();
    setProject((prev) =>
      prev
        ? {
            ...prev,
            completionPercentOverride: parsed,
            completionPercentOverriddenBy: 'Sarah Chen', // current mock user
            completionPercentOverriddenAt: now,
            completionPercentMode: 'manual',
          }
        : prev
    );
    setCompletionPercentEditing(false);
  };

  const handleCompletionPercentEditCancel = () => {
    setCompletionPercentEditing(false);
  };

  /** Switch calculation mode in the Billing Settings drawer. */
  const handleCompletionModeChange = (mode: 'manual' | 'based-on-time') => {
    setProject((prev) => {
      if (!prev) return prev;
      if (mode === 'based-on-time') {
        // Recalculate from hours; clear any override
        return {
          ...prev,
          completionPercentMode: 'based-on-time',
          completionPercentOverride: undefined,
          completionPercentOverriddenBy: undefined,
          completionPercentOverriddenAt: undefined,
        };
      }
      // switching to manual — freeze current value
      return {
        ...prev,
        completionPercentMode: 'manual',
      };
    });
  };

  // Calculate next invoice generation date based on settings (for drawer)
  const getNextInvoiceDateForDrawer = () => {
    if (!drawerSettings.autoGenerateEnabled) return null;

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    if (drawerSettings.billingFrequency === "weekly") {
      const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      const targetDay = daysOfWeek.indexOf(drawerSettings.weeklyDay);
      const currentDay = today.getDay();

      let daysUntilTarget = targetDay - currentDay;
      if (daysUntilTarget <= 0) {
        daysUntilTarget += 7;
      }

      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + daysUntilTarget);
      return nextDate;
    }

    // Monthly patterns
    let targetDate = new Date(currentYear, currentMonth, 1);

    switch (drawerSettings.monthlyPattern) {
      case "specific-day": {
        const day = parseInt(drawerSettings.monthlyDay, 10);
        targetDate.setDate(Math.min(day, new Date(currentYear, currentMonth + 1, 0).getDate()));
        break;
      }
      case "last-day":
        targetDate = new Date(currentYear, currentMonth + 1, 0);
        break;
      case "first-working-day": {
        targetDate.setDate(1);
        while (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
          targetDate.setDate(targetDate.getDate() + 1);
        }
        break;
      }
      case "last-working-day": {
        targetDate = new Date(currentYear, currentMonth + 1, 0);
        while (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
          targetDate.setDate(targetDate.getDate() - 1);
        }
        break;
      }
    }

    if (targetDate <= today) {
      targetDate.setMonth(targetDate.getMonth() + 1);

      if (drawerSettings.monthlyPattern === "specific-day") {
        const day = parseInt(drawerSettings.monthlyDay, 10);
        const lastDayOfMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0).getDate();
        targetDate.setDate(Math.min(day, lastDayOfMonth));
      } else if (drawerSettings.monthlyPattern === "last-day") {
        targetDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
      } else if (drawerSettings.monthlyPattern === "first-working-day") {
        targetDate.setDate(1);
        while (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
          targetDate.setDate(targetDate.getDate() + 1);
        }
      } else if (drawerSettings.monthlyPattern === "last-working-day") {
        targetDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
        while (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
          targetDate.setDate(targetDate.getDate() - 1);
        }
      }
    }

    return targetDate;
  };

  // Calculate next invoice generation date based on settings
  const getNextInvoiceDate = () => {
    if (!projectSettings.autoGenerateEnabled) return null;

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    if (projectSettings.billingFrequency === "weekly") {
      const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      const targetDay = daysOfWeek.indexOf(projectSettings.weeklyDay);
      const currentDay = today.getDay();

      let daysUntilTarget = targetDay - currentDay;
      if (daysUntilTarget <= 0) {
        daysUntilTarget += 7; // Next week
      }

      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + daysUntilTarget);
      return nextDate;
    }

    // Monthly patterns
    if (projectSettings.billingFrequency === "monthly") {
      let nextDate = new Date(currentYear, currentMonth, 1);

      switch (projectSettings.monthlyPattern) {
        case "specific-day": {
          const day = parseInt(projectSettings.monthlyDay);
          const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
          const actualDay = Math.min(day, daysInMonth);
          nextDate.setDate(actualDay);

          // If the date has passed this month, move to next month
          if (nextDate <= today) {
            nextDate = new Date(currentYear, currentMonth + 1, 1);
            const nextMonthDays = new Date(currentYear, currentMonth + 2, 0).getDate();
            nextDate.setDate(Math.min(day, nextMonthDays));
          }
          break;
        }

        case "last-day": {
          nextDate = new Date(currentYear, currentMonth + 1, 0);
          if (nextDate <= today) {
            nextDate = new Date(currentYear, currentMonth + 2, 0);
          }
          break;
        }

        case "first-working-day": {
          nextDate = new Date(currentYear, currentMonth, 1);
          if (nextDate <= today) {
            nextDate = new Date(currentYear, currentMonth + 1, 1);
          }
          // Skip to Monday if weekend
          while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
            nextDate.setDate(nextDate.getDate() + 1);
          }
          break;
        }

        case "last-working-day": {
          nextDate = new Date(currentYear, currentMonth + 1, 0);
          if (nextDate <= today) {
            nextDate = new Date(currentYear, currentMonth + 2, 0);
          }
          // Skip back to Friday if weekend
          while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
            nextDate.setDate(nextDate.getDate() - 1);
          }
          break;
        }
      }

      return nextDate;
    }

    return null;
  };

  const validateSettings = () => {
    // Validate inputs
    if (projectSettings.expenseRecharge === "markup" && !projectSettings.expenseMarkupPercent) {
      toast.error("Please enter expense markup percentage");
      return false;
    }
    if (projectSettings.purchaseRecharge === "markup" && !projectSettings.purchaseMarkupPercent) {
      toast.error("Please enter purchase markup percentage");
      return false;
    }
    if (projectSettings.notToExceedCapEnabled && !projectSettings.notToExceedCapAmount) {
      toast.error("Please enter invoice cap amount");
      return false;
    }
    return true;
  };

  // Warn user about unsaved changes when navigating away
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Listen for edit mode trigger from header Edit button
  useEffect(() => {
    const handleEditMode = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.projectId === projectId) {
        handleEnterEditMode();
      }
    };

    window.addEventListener("project-edit-mode", handleEditMode);
    return () => window.removeEventListener("project-edit-mode", handleEditMode);
  }, [projectId]);

  useEffect(() => {
    let ignore = false;
    async function loadData() {
      setLoading(true);
      try {
        const [currentProject, clientsData, employeesData, resourceAllocationsData] = await Promise.all([
          fetchProjectById(numericProjectId),
          fetchClients("projects"),
          fetchEmployees("projects"),
          fetchResourceAllocations("projects"),
        ]);

        if (ignore) return;

        if (currentProject) {
          setProject(currentProject);
          setClients(clientsData); // Store all clients for the dropdown
          // Get the first client ID from the comma-separated list
          const firstClientId = currentProject.clientIds?.split(",")[0]?.trim();
          setClient(clientsData.find((c) => c.id === parseInt(firstClientId || "0", 10)) || null);
          setProjectManager(employeesData.find((e) => e.id === currentProject.projectManagerId) || null);

          // Get data mode to determine whether to use API or mock data
          const dataMode = getDataMode();

          // Get resource allocations for this project
          const projectAllocations = resourceAllocationsData.filter((r) => r.projectId === numericProjectId);
          setResourceAllocations(projectAllocations);

          if (dataMode === "live") {
            // Live mode - fetch data from API
            try {
              const expenseOptions: FetchExpensesOptions = {
                includeApprovedExpenses: includeApprovedExpenses,
                includeCreditCardExpenses: includeCreditCardExpenses,
              };
              const [apiTimeEntries, apiExpenses, apiMilestones, apiChangeRequests] = await Promise.all([
                fetchTimeEntriesByProjectId(numericProjectId),
                fetchExpensesByProjectId(
                  numericProjectId,
                  currentProject.code,
                  currentCompany,
                  "expenses",
                  expenseOptions,
                ),
                fetchMilestonesByProjectId(numericProjectId),
                fetchChangeRequestsByProjectId(numericProjectId),
              ]);
              setTimeEntries(apiTimeEntries);
              setExpenses(apiExpenses);
              setMilestones(apiMilestones);
              setChangeRequests(apiChangeRequests);

              // Calculate actual hours per employee from time entries
              const timeByEmployee = new Map<number, number>();
              apiTimeEntries.forEach((entry: TimeEntry) => {
                const current = timeByEmployee.get(entry.employeeId) || 0;
                timeByEmployee.set(entry.employeeId, current + entry.hours);
              });

              // Build a map of resource allocations by employee for quick lookup
              const allocationsByEmployee = new Map<number, (typeof projectAllocations)[0]>();
              projectAllocations.forEach((alloc) => {
                allocationsByEmployee.set(alloc.employeeId, alloc);
              });

              // Generate team members from all employees fetched from the backend
              // This ensures the Team Management table shows employees even when there are no resource allocations
              const liveTeamMembers: TeamMember[] = employeesData.map((employee) => {
                const allocation = allocationsByEmployee.get(employee.id);
                const actualHours = timeByEmployee.get(employee.id) || 0;

                // Use allocation data if available, otherwise default to 0
                const allocatedHours = allocation?.allocatedHours || 0;
                const remaining = allocatedHours - actualHours;
                const utilisationPercent = allocatedHours > 0 ? (actualHours / allocatedHours) * 100 : 0;

                // Determine status based on utilisation
                let status: TeamMember["status"];
                if (allocatedHours > 0 && utilisationPercent > 100) {
                  status = "Over Allocated";
                } else if (allocatedHours > 0 && utilisationPercent > 85) {
                  status = "At Risk";
                } else {
                  status = "On Track";
                }

                return {
                  id: allocation?.id || employee.id,
                  employeeId: employee.id,
                  name: employee.name,
                  email: employee.email || "",
                  role: allocation?.role || employee.role || "Unassigned",
                  avatar: employee.photo,
                  allocatedHours,
                  actualHours,
                  remaining,
                  startDate: allocation?.startDate || "",
                  endDate: allocation?.endDate || "",
                  status,
                  utilisationPercent,
                };
              });

              // Set team members from employees
              setTeamMembers(liveTeamMembers);

              // Billing data is lazy-loaded when the Billing tab is activated
              // (see useEffect for activeTab === "billing" below)
            } catch (error) {
              console.error("Failed to fetch project details from API:", error);
              setTimeEntries([]);
              setExpenses([]);
              setMilestones([]);
              setChangeRequests([]);
              setTeamMembers([]);
              setBillingWipItems([]);
              setBillingInvoices([]);
            }
          } else {
            // Demo mode - generate mock data
            // Mock time entries - include both allocated and unallocated employees
            const mockTimeEntries: TimeEntry[] = projectAllocations.slice(0, 10).map((alloc, i) => {
              const isApproved = Math.random() > 0.3;
              const entryDate = new Date(Date.now() - i * 86400000);

              return {
                id: numericProjectId * 10000 + i,
                externalTimesheetId: `TS-2024-${1000 + i}`,
                projectId: numericProjectId,
                employeeId: alloc.employeeId,
                date: entryDate.toISOString().split("T")[0],
                hours: 6 + Math.random() * 3,
                description: "Development work",
                billable: Math.random() > 0.2,
                approved: isApproved,
                approvalHistory: [
                  {
                    date: new Date(entryDate.getTime() + 3600000).toISOString(),
                    action: "Submitted for Approval",
                    byUser: employeesData.find((e) => e.id === alloc.employeeId)?.name || "Employee",
                    status: "Pending Approval",
                  },
                  ...(isApproved
                    ? [
                        {
                          date: new Date(entryDate.getTime() + 7200000).toISOString(),
                          action: "Approved",
                          byUser: "Project Manager",
                          toUser: "Finance Team",
                          stage: "Manager Approval",
                          status: "Approved",
                        },
                      ]
                    : []),
                ],
                costRate: 50 + Math.random() * 30,
                billRate: 100 + Math.random() * 60,
              };
            });

            // Add time entries from employees NOT allocated to the project (to demonstrate unallocated tracking)
            const mockAllocatedEmployeeIds = new Set(projectAllocations.map((a) => a.employeeId));
            const unallocatedEmployees = employeesData.filter((e) => !mockAllocatedEmployeeIds.has(e.id)).slice(0, 2);

            unallocatedEmployees.forEach((employee, i) => {
              // Add 3-5 time entries for each unallocated employee
              const entryCount = 3 + Math.floor(Math.random() * 3);
              for (let j = 0; j < entryCount; j++) {
                const isApproved = Math.random() > 0.4;
                const entryDate = new Date(Date.now() - (i * 7 + j) * 86400000);

                mockTimeEntries.push({
                  id: numericProjectId * 10000 + 1000 + i * 10 + j,
                  externalTimesheetId: `TS-2024-${2000 + i * 10 + j}`,
                  projectId: numericProjectId,
                  employeeId: employee.id,
                  date: entryDate.toISOString().split("T")[0],
                  hours: 4 + Math.random() * 4,
                  description: "Ad-hoc support work",
                  billable: Math.random() > 0.3,
                  approved: isApproved,
                  approvalHistory: [
                    {
                      date: new Date(entryDate.getTime() + 3600000).toISOString(),
                      action: "Submitted for Approval",
                      byUser: employee.name,
                      status: "Pending Approval",
                    },
                    ...(isApproved
                      ? [
                          {
                            date: new Date(entryDate.getTime() + 7200000).toISOString(),
                            action: "Approved",
                            byUser: "Project Manager",
                            toUser: "Finance Team",
                            stage: "Manager Approval",
                            status: "Approved",
                          },
                        ]
                      : []),
                  ],
                  costRate: 50 + Math.random() * 30,
                  billRate: 100 + Math.random() * 60,
                });
              }
            });

            setTimeEntries(mockTimeEntries);

            // Mock expenses
            const mockExpenses: Expense[] = [
              {
                id: numericProjectId * 1000 + 1,
                projectId: numericProjectId,
                employeeId: projectAllocations[0]?.employeeId || "e1",
                date: new Date(Date.now() - 5 * 86400000).toISOString().split("T")[0],
                category: "Travel",
                amount: 450,
                description: "Client site visit",
                billable: true,
                approved: true,
                approvalHistory: [
                  {
                    date: new Date(Date.now() - 5 * 86400000 + 3600000).toISOString(),
                    action: "Submitted for Approval",
                    byUser:
                      employeesData.find((e) => e.id === (projectAllocations[0]?.employeeId || "e1"))?.name ||
                      "Employee",
                    status: "Pending Approval",
                  },
                  {
                    date: new Date(Date.now() - 5 * 86400000 + 7200000).toISOString(),
                    action: "Approved",
                    byUser: "Project Manager",
                    toUser: "Finance Team",
                    stage: "Manager Approval",
                    status: "Approved",
                  },
                ],
              },
              {
                id: numericProjectId * 1000 + 2,
                projectId: numericProjectId,
                employeeId: projectAllocations[1]?.employeeId || "e2",
                date: new Date(Date.now() - 10 * 86400000).toISOString().split("T")[0],
                category: "Software",
                amount: 299,
                description: "Development tools licence",
                billable: true,
                approved: false,
                approvalHistory: [
                  {
                    date: new Date(Date.now() - 10 * 86400000 + 3600000).toISOString(),
                    action: "Submitted for Approval",
                    byUser:
                      employeesData.find((e) => e.id === (projectAllocations[1]?.employeeId || "e2"))?.name ||
                      "Employee",
                    status: "Pending Approval",
                  },
                ],
              },
            ];
            setExpenses(mockExpenses);

            // Mock milestones
            const mockMilestones: Milestone[] = [
              {
                id: numericProjectId * 100 + 1,
                projectId: numericProjectId,
                name: "Project Kickoff",
                dueDate: currentProject.startDate,
                completedDate: currentProject.startDate,
                status: "completed",
                paymentAmount: currentProject.budgetRevenue * 0.2,
                paymentReceived: true,
              },
              {
                id: numericProjectId * 100 + 2,
                projectId: numericProjectId,
                name: "Phase 1 Delivery",
                dueDate: new Date(new Date(currentProject.startDate).getTime() + 60 * 86400000)
                  .toISOString()
                  .split("T")[0],
                completedDate: new Date(new Date(currentProject.startDate).getTime() + 58 * 86400000)
                  .toISOString()
                  .split("T")[0],
                status: "completed",
                paymentAmount: currentProject.budgetRevenue * 0.3,
                paymentReceived: true,
              },
              {
                id: numericProjectId * 100 + 3,
                projectId: numericProjectId,
                name: "Phase 2 Delivery",
                dueDate: new Date(new Date(currentProject.startDate).getTime() + 120 * 86400000)
                  .toISOString()
                  .split("T")[0],
                status: currentProject.completionPercent > 70 ? "completed" : "pending",
                completedDate:
                  currentProject.completionPercent > 70
                    ? new Date(new Date(currentProject.startDate).getTime() + 118 * 86400000)
                        .toISOString()
                        .split("T")[0]
                    : undefined,
                paymentAmount: currentProject.budgetRevenue * 0.3,
                paymentReceived: false,
              },
              {
                id: numericProjectId * 100 + 4,
                projectId: numericProjectId,
                name: "Final Delivery",
                dueDate: currentProject.endDate,
                status: currentProject.status === "completed" ? "completed" : "pending",
                completedDate: currentProject.status === "completed" ? currentProject.endDate : undefined,
                paymentAmount: currentProject.budgetRevenue * 0.2,
                paymentReceived: false,
              },
            ];
            setMilestones(mockMilestones);

            // Mock change requests
            const mockChangeRequests: ChangeRequest[] = [
              {
                id: numericProjectId * 100 + 1,
                projectId: numericProjectId,
                title: "Additional feature request",
                description: "Client requested additional reporting functionality",
                requestedBy: "Client",
                requestedDate: new Date(Date.now() - 15 * 86400000).toISOString().split("T")[0],
                status: "approved",
                impactBudget: 15000,
                impactSchedule: 10,
                approvedBy: currentProject.projectManagerId,
                approvedDate: new Date(Date.now() - 10 * 86400000).toISOString().split("T")[0],
              },
              {
                id: numericProjectId * 100 + 2,
                projectId: numericProjectId,
                title: "Scope clarification",
                description: "Clarification on integration requirements",
                requestedBy: "Project Team",
                requestedDate: new Date(Date.now() - 5 * 86400000).toISOString().split("T")[0],
                status: "pending",
                impactBudget: 0,
                impactSchedule: 0,
              },
            ];
            setChangeRequests(mockChangeRequests);

            // Generate comprehensive team member data with mock hours
            const mockTeamMembers: TeamMember[] = projectAllocations.map((alloc) => {
              const employee = employeesData.find((e) => e.id === alloc.employeeId);
              const allocatedHours = Math.floor(Math.random() * 200) + 100; // 100-300 hours
              const actualHours = Math.floor(allocatedHours * (0.3 + Math.random() * 0.8)); // 30-110% of allocated
              const remaining = allocatedHours - actualHours;
              const utilisationPercent = (actualHours / allocatedHours) * 100;

              // Determine status based on utilisation only
              let status: TeamMember["status"];

              if (utilisationPercent > 100) {
                status = "Over Allocated";
              } else if (utilisationPercent > 85) {
                status = "At Risk";
              } else {
                status = "On Track";
              }

              // Generate avatar URL using UI Avatars service
              const name = employee?.name || "Unknown";
              const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

              return {
                id: alloc.id,
                employeeId: alloc.employeeId,
                name: name,
                email: employee?.email || "",
                role: alloc.role,
                avatar: avatarUrl,
                allocatedHours,
                actualHours,
                remaining,
                startDate: alloc.startDate,
                endDate: alloc.endDate,
                status,
                utilisationPercent,
              };
            });

            // Find employees who logged time but weren't allocated
            const demoAllocatedEmployeeIds = new Set(mockTeamMembers.map((tm) => tm.employeeId));
            const demoUnallocatedWithTime: TeamMember[] = [];

            // Group time entries by employee
            const demoTimeByEmployee = new Map<number, number>();
            mockTimeEntries.forEach((entry) => {
              if (!demoAllocatedEmployeeIds.has(entry.employeeId)) {
                const current = demoTimeByEmployee.get(entry.employeeId) || 0;
                demoTimeByEmployee.set(entry.employeeId, current + entry.hours);
              }
            });

            // Create TeamMember entries for unallocated employees who logged time
            demoTimeByEmployee.forEach((hours, employeeId) => {
              const employee = employeesData.find((e) => e.id === employeeId);
              if (employee) {
                const name = employee.name;
                const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

                demoUnallocatedWithTime.push({
                  id: `unalloc-${employeeId}`,
                  employeeId: employee.id,
                  name: name,
                  email: employee.email || "",
                  role: employee.role || "Unassigned", // Use employee.role instead of employee.position
                  avatar: avatarUrl,
                  allocatedHours: 0, // Not allocated
                  actualHours: hours,
                  remaining: -hours, // Negative because they worked without allocation
                  startDate: "", // No allocation dates
                  endDate: "",
                  status: "Over Allocated", // Working without allocation
                  utilisationPercent: 0, // Can't calculate without allocation
                });
              }
            });

            // Combine allocated and unallocated team members
            setTeamMembers([...mockTeamMembers, ...demoUnallocatedWithTime]);
          }

          // Fetch cost centres - use API in live mode, mock data in demo mode
          // Note: dataMode is already defined above
          if (dataMode === "live") {
            try {
              const apiCostCentres = await fetchCostCentresByProjectId(numericProjectId);
              setCostCentres(apiCostCentres);
            } catch (error) {
              console.error("Failed to fetch cost centres from API:", error);
              setCostCentres([]);
            }
          } else {
            // Helper arrays for variety (used by both flat and hierarchical views)
            const types = ["Functional Area", "Administrative", "Technical Support", "Operations", "Support Services"];
            const statuses: Array<"Not Started" | "In Progress" | "On Hold" | "Completed" | "Cancelled"> = [
              "Not Started",
              "In Progress",
              "On Hold",
              "Completed",
              "Cancelled",
            ];
            const departments = [
              "Engineering",
              "Design",
              "Quality Assurance",
              "PMO",
              "IT Operations",
              "Marketing",
              "Sales",
              "Finance",
              "HR",
              "Legal",
            ];
            const locations = [
              "London Office",
              "Manchester Office",
              "Birmingham Office",
              "Head Office",
              "Data Centre",
              "Edinburgh Office",
              "Bristol Office",
              "Leeds Office",
              "Remote",
              "Client Site",
            ];
            const priorities = ["High", "Medium", "Low"];
            const owners = [
              "John Smith",
              "Sarah Johnson",
              "Mike Davis",
              "Emily Brown",
              "David Wilson",
              "Alice Chen",
              "Bob Martinez",
              "Carol White",
              "Dan Brown",
              "Emma Wilson",
            ];

            // Mock cost centres data for demo mode - 50 records for scrolling test
            const mockCostCentres: CostCentre[] = [];

            // Generate 50 cost centres
            for (let i = 1; i <= 50; i++) {
              const code = `CC-${String(i).padStart(3, "0")}`;
              const typeIndex = (i - 1) % types.length;
              const statusIndex = (i - 1) % statuses.length;
              const deptIndex = (i - 1) % departments.length;
              const locIndex = (i - 1) % locations.length;
              const priorityIndex = (i - 1) % priorities.length;
              const ownerIndex = (i - 1) % owners.length;

              // Vary the budget allocation
              const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
              const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3); // 85% to 115% of budget
              const committedFactor = budgetFactor * 0.1;

              mockCostCentres.push({
                id: -Date.now() - i,
                code: code,
                name: `Cost Centre ${i}`,
                description: `Description for cost centre ${code}`,
                type: types[typeIndex],
                status: statuses[statusIndex],
                billable: i % 3 !== 0, // 2/3 are billable
                costsBudget: currentProject.budgetCost * budgetFactor,
                actualCosts: currentProject.actualCost * actualFactor,
                committedCosts: currentProject.budgetCost * committedFactor,
                revenueBudget: currentProject.budgetRevenue * budgetFactor,
                actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                hoursBudget: Math.round(budgetFactor * 10000),
                actualHours: Math.round(actualFactor * 9500),
                owner: owners[ownerIndex],
                lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                createdAt: new Date(currentProject.startDate),
                updatedAt: new Date(Date.now() - (i % 10) * 86400000),
                customFields: {
                  Department: departments[deptIndex],
                  Location: locations[locIndex],
                  Priority: priorities[priorityIndex],
                  "Start Date": new Date(currentProject.startDate),
                  "End Date": new Date(currentProject.endDate),
                  "Requires Approval": i % 2 === 0,
                  "Approval Threshold": 1000 + i * 100,
                  "Cost Code": `${code}-${departments[deptIndex].substring(0, 3).toUpperCase()}`,
                  "GL Account": `${6100 + (i % 5) * 100}-${1000 + (i % 10) * 100}`,
                  "Project Phase": `Phase ${(i % 5) + 1}`,
                },
              });
            }
            setCostCentres(mockCostCentres);

            // Mock hierarchical cost centres data - includes all 50 cost centre codes
            const mockHierarchicalCostCentres: HierarchicalCostCentre[] = [];

            // Create 5 parent departments, each with 10 cost centres
            const parentDepartments: Array<{ code: string; name: string; range: [number, number] }> = [
              { code: "CC-DEPT-01", name: "Engineering Department", range: [1, 10] },
              { code: "CC-DEPT-02", name: "Design & Creative", range: [11, 20] },
              { code: "CC-DEPT-03", name: "Operations", range: [21, 30] },
              { code: "CC-DEPT-04", name: "Support Services", range: [31, 40] },
              { code: "CC-DEPT-05", name: "Administration", range: [41, 50] },
            ];

            parentDepartments.forEach((dept, deptIndex) => {
              // Calculate totals for parent
              const deptBudgetFactor = 0.2; // Each department gets 20% of total budget

              // Add parent department
              mockHierarchicalCostCentres.push({
                id: -Date.now() - 100 - deptIndex,
                code: dept.code,
                name: dept.name,
                billable: true,
                costsBudget: currentProject.budgetCost * deptBudgetFactor,
                actualCosts: currentProject.actualCost * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                committedCosts: currentProject.budgetCost * deptBudgetFactor * 0.05,
                revenueBudget: currentProject.budgetRevenue * deptBudgetFactor,
                actualRevenue: currentProject.actualRevenue * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                hoursBudget: Math.round(deptBudgetFactor * 10000),
                actualHours: Math.round(deptBudgetFactor * 9500 * (0.9 + Math.random() * 0.2)),
                parentId: null,
              });

              // Add child cost centres (CC-001 to CC-050)
              for (let i = dept.range[0]; i <= dept.range[1]; i++) {
                const code = `CC-${String(i).padStart(3, "0")}`;
                const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
                const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3);
                const committedFactor = budgetFactor * 0.1;

                const statusIndex = (i - 1) % statuses.length;
                const ownerIndex = (i - 1) % owners.length;

                mockHierarchicalCostCentres.push({
                  id: -Date.now() - i,
                  code: code,
                  name: `Cost Centre ${i}`,
                  type: types[(i - 1) % types.length],
                  status: statuses[statusIndex],
                  billable: i % 3 !== 0,
                  costsBudget: currentProject.budgetCost * budgetFactor,
                  actualCosts: currentProject.actualCost * actualFactor,
                  committedCosts: currentProject.budgetCost * committedFactor,
                  revenueBudget: currentProject.budgetRevenue * budgetFactor,
                  actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                  hoursBudget: Math.round(budgetFactor * 10000),
                  actualHours: Math.round(actualFactor * 9500),
                  owner: owners[ownerIndex],
                  lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                  parentId: -Date.now() - 100 - deptIndex,
                });
              }
            });

            setHierarchicalCostCentres(mockHierarchicalCostCentres);
          }
        }

        setEmployees(employeesData);
      } catch (error) {
        handleApiError(error, `/api/projects/${projectId}`, "GET");
      } finally {
        setLoading(false);
      }
    }
    loadData();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Sync expense checkbox defaults when company settings change
  useEffect(() => {
    if (currentCompany) {
      setIncludeApprovedExpenses(currentCompany.approvedExpensesEnabled ?? true);
      setIncludeCreditCardExpenses(currentCompany.creditCardExpensesEnabled ?? false);
    }
  }, [currentCompany?.id]);

  // Re-fetch expenses when checkbox selections change
  useEffect(() => {
    if (!project || loading) return;
    const dataMode = getDataMode();
    if (dataMode !== "live") return;

    async function refetchExpenses() {
      setExpensesLoading(true);
      try {
        const expenseOpts: FetchExpensesOptions = {
          includeApprovedExpenses: includeApprovedExpenses,
          includeCreditCardExpenses: includeCreditCardExpenses,
        };
        const refreshedExpenses = await fetchExpensesByProjectId(
          numericProjectId,
          project!.code,
          currentCompany,
          "expenses",
          expenseOpts,
        );
        setExpenses(refreshedExpenses);
      } catch (error) {
        console.error("Failed to refetch expenses:", error);
      } finally {
        setExpensesLoading(false);
      }
    }
    refetchExpenses();
  }, [includeApprovedExpenses, includeCreditCardExpenses]);

  // Lazy-load billing data when the Billing tab is activated
  // This avoids fetching 20k+ WIP items on initial project detail load (Overview tab)
  const billingDataLoadedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== "billing") return;
    if (loading || !project) return;
    const dataMode = getDataMode();
    if (dataMode !== "live") return;
    // Only fetch on first activation; subsequent refreshes use refetchBillingData
    if (billingDataLoadedRef.current) return;
    billingDataLoadedRef.current = true;

    async function fetchBilling() {
      try {
        setBillingLoading(true);
        const [apiBillingWipResponse, apiBillingInvoices] = await Promise.all([
          fetchBillingWipItemsPaged(numericProjectId, 0, 1, wipItemsPerPage),
          fetchBillingInvoices(numericProjectId),
        ]);
        setBillingWipItems(apiBillingWipResponse.items);
        setWipTotalCount(apiBillingWipResponse.totalCount);
        if (apiBillingWipResponse.summary) setWipSummary(apiBillingWipResponse.summary);
        setBillingInvoices(apiBillingInvoices);
      } catch (billingError) {
        console.error("Failed to fetch billing data from API:", billingError);
        setBillingWipItems([]);
        setWipTotalCount(0);
        setWipSummary({ totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 });
        setBillingInvoices([]);
      } finally {
        setBillingLoading(false);
      }
    }
    fetchBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loading, project]);

  // Billing action handlers
  const refetchBillingData = useCallback(async () => {
    if (dataMode !== "live") return;
    try {
      setBillingLoading(true);
      const [updatedWipResponse, updatedInvoices] = await Promise.all([
        fetchBillingWipItemsPaged(numericProjectId, 0, wipCurrentPage, wipItemsPerPage),
        fetchBillingInvoices(numericProjectId),
      ]);
      setBillingWipItems(updatedWipResponse.items);
      setWipTotalCount(updatedWipResponse.totalCount);
      if (updatedWipResponse.summary) setWipSummary(updatedWipResponse.summary);
      setBillingInvoices(updatedInvoices);
    } catch (error) {
      console.error("Failed to refresh billing data:", error);
    } finally {
      setBillingLoading(false);
    }
  }, [dataMode, numericProjectId, wipCurrentPage, wipItemsPerPage]);

  const handleCreateDraft = useCallback(async (description: string, notes?: string) => {
    if (dataMode !== "live") return;
    // Get selected WIP item IDs (extract numeric ID from "wip-{id}" format)
    const wipItemIds = selectedWipItems
      .map((id) => {
        const numId = parseInt(id.replace("wip-", ""), 10);
        return isNaN(numId) ? null : numId;
      })
      .filter((id): id is number => id !== null);
    
    if (wipItemIds.length === 0) {
      console.warn("No WIP items selected for draft creation");
      return;
    }

    try {
      setBillingActionLoading(true);
      await createBillingDraft(numericProjectId, wipItemIds, description, notes);
      setSelectedWipItems([]); // Clear selection
      setCreateDraftDialogOpen(false);
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to create draft:", error);
      handleApiError(error, `/api/billing/${numericProjectId}/drafts`, "POST");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, numericProjectId, selectedWipItems, refetchBillingData, handleApiError]);

  const handleDeleteDraft = useCallback(async (invoiceId: number) => {
    if (dataMode !== "live") return;
    try {
      setBillingActionLoading(true);
      await deleteBillingDraft(invoiceId);
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to delete draft:", error);
      handleApiError(error, `/api/billing/drafts/${invoiceId}`, "DELETE");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, refetchBillingData, handleApiError]);

  const handlePostToFinancials = useCallback(async (invoiceId: number) => {
    if (dataMode !== "live") return;
    try {
      setBillingActionLoading(true);
      const result = await postBillingDraftToFinancials(invoiceId);

      if (result.success) {
        toast.success("Invoice posted to Access Financials", {
          description: result.financialsIds?.length
            ? `Transaction ID${result.financialsIds.length > 1 ? "s" : ""}: ${result.financialsIds.join(", ")}`
            : undefined,
        });
        await refetchBillingData();
      } else if (result.validationErrors && result.validationErrors.length > 0) {
        const errorMessages = result.validationErrors
          .map((e) => {
            const details = e.details?.map((d) => `${d.field}: ${d.value}`).join(", ");
            return details ? `${e.message} (${details})` : e.message;
          })
          .join("\n");
        toast.error("Financials validation failed", {
          description: errorMessages,
          duration: 10000,
        });
      } else {
        toast.error("Failed to post invoice to Financials", {
          description: result.error || "An unexpected error occurred",
          duration: 8000,
        });
      }
    } catch (error) {
      console.error("Failed to post to financials:", error);
      handleApiError(error, `/api/billing/drafts/${invoiceId}/post`, "POST");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, refetchBillingData, handleApiError]);

  const handleVoidInvoice = useCallback(async (invoiceId: number, reason: string) => {
    if (dataMode !== "live") return;
    try {
      setBillingActionLoading(true);
      await voidBillingInvoice(invoiceId, reason);
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to void invoice:", error);
      handleApiError(error, `/api/billing/invoices/${invoiceId}/void`, "POST");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, refetchBillingData, handleApiError]);

  // Listen for edit mode event from the Edit button in AppLayout
  useEffect(() => {
    const handleEditMode = (event: Event) => {
      const customEvent = event as CustomEvent<{ projectId: string }>;
      console.log("Edit mode event received:", customEvent.detail, "Current projectId:", projectId);
      if (customEvent.detail?.projectId === projectId) {
        handleEnterEditMode();
      }
    };

    window.addEventListener("project-edit-mode", handleEditMode);
    return () => window.removeEventListener("project-edit-mode", handleEditMode);
  }, [projectId, handleEnterEditMode]);

  // Warn user about unsaved changes when navigating away
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load recent client IDs from localStorage
  useEffect(() => {
    const storedRecentClients = localStorage.getItem("recentClientIds");
    const storedRecentPMs = localStorage.getItem("recentProjectManagerIds");
    if (storedRecentPMs) {
      try {
        const parsed = JSON.parse(storedRecentPMs);
        setRecentProjectManagerIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error("Failed to parse recent project manager IDs:", error);
        setRecentProjectManagerIds([]);
      }
    }
    if (storedRecentClients) {
      try {
        const parsed = JSON.parse(storedRecentClients);
        setRecentClientIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error("Failed to parse recent client IDs:", error);
        setRecentClientIds([]);
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Skeleton for Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-full mb-1" />
                <Skeleton className="h-3 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Skeleton for Tabs */}
        <Card>
          <CardHeader>
            <div className="flex space-x-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
            <div className="pt-4">
              <Skeleton className="h-64 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div className="text-muted-foreground">Project not found</div>
        <Button onClick={() => (onBack ? onBack() : navigate(ROUTES.PROJECTS))}>Back to Projects</Button>
      </div>
    );
  }

  const getEmployeeName = (employeeId: number) => {
    return employees.find((e) => e.id === employeeId)?.name || "Unknown";
  };

  const getStatusBadgeVariant = (
    status: TeamMember["status"],
    utilization?: number,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "On Track":
        // Use different colors based on utilization
        if (utilization !== undefined) {
          if (utilization > 75) {
            return "default"; // Blue for higher utilization (75-85%)
          } else if (utilization > 50) {
            return "outline"; // Outline for medium utilization (50-75%)
          } else {
            return "secondary"; // Gray for low utilization (0-50%)
          }
        }
        return "default";
      case "At Risk":
        return "destructive";
      case "Over Allocated":
        return "destructive";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: TeamMember["status"] | string): string => {
    // Normalize status to lowercase for comparison
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case "completed":
        return "text-neutral-600";
      case "on track":
      case "on-track":
        return "text-success-600";
      case "active":
      case "in-progress":
        return "text-info-600";
      case "at risk":
      case "at-risk":
        return "text-warning-600";
      case "over allocated":
      case "over-allocated":
        return "text-error-600";
      case "planning":
      case "on-hold":
        return "text-neutral-500";
      default:
        return "text-neutral-600";
    }
  };

  const handleSort = (field: keyof TeamMember) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleRemoveTeamMember = async (member: TeamMember) => {
    // Only allow removal if no hours have been logged
    if (member.actualHours > 0) {
      toast.error("Cannot remove team member who has logged hours");
      return;
    }

    const dataMode = getDataMode();

    // In live mode, call the API to delete the resource allocation
    if (dataMode === "live" && member.id && member.id !== member.employeeId) {
      try {
        const memberId = typeof member.id === "number" ? member.id : parseInt(String(member.id), 10);
        await deleteResourceAllocation(memberId);
      } catch (error) {
        console.error("Failed to delete resource allocation:", error);
        toast.error("Failed to remove team member from project");
        return;
      }
    }

    // Remove the team member from local state
    setTeamMembers((prev) => prev.filter((tm) => tm.id !== member.id));

    // Update the budget data to reflect the removed allocation
    if (enrichedBudgetData?.resourceBudget?.byRole && member.allocatedHours > 0) {
      const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map((r) => {
        if (r.role === member.role) {
          return {
            ...r,
            allocatedHours: Math.max(0, r.allocatedHours - member.allocatedHours),
          };
        }
        return r;
      });

      // Update local budget data
      setLocalBudgetData({
        ...enrichedBudgetData,
        resourceBudget: {
          ...enrichedBudgetData.resourceBudget,
          byRole: updatedRoles,
        },
      });
    }

    toast.success(`${member.name} removed from project`);
  };

  const sortedTeamMembers = [...teamMembers].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const totalBillableHours = timeEntries.filter((t) => t.billable).reduce((sum, t) => sum + t.hours, 0);

  const totalNonBillableHours = timeEntries.filter((t) => !t.billable).reduce((sum, t) => sum + t.hours, 0);

  const totalExpenseAmount = expenses.reduce((sum, e) => sum + e.amount, 0);

  const completedMilestones = milestones.filter((m) => m.status === "completed").length;
  const overdueMilestones = milestones.filter((m) => m.status === "overdue").length;

  const activeResources = resourceAllocations.filter((r) => r.status === "confirmed").length;

  const pendingChangeRequests = changeRequests.filter((cr) => cr.status === "pending").length;
  const approvedChangeRequests = changeRequests.filter((cr) => cr.status === "approved").length;

  // Create default empty budget data for new projects that don't have budget data yet
  const getDefaultBudgetData = (): BudgetData => ({
    projectId: numericProjectId,
    projectName: project?.name || "New Project",
    projectType: (project?.type as "fixed-price" | "time-and-materials" | "retainer") || "fixed-price",
    summary: {
      revenue: {
        budget: 0,
        actual: 0,
        variance: 0,
        variancePercent: 0,
        progressPercent: 0,
        trend: "stable",
        status: "green",
      },
      costs: {
        budget: 0,
        actual: 0,
        variance: 0,
        variancePercent: 0,
        progressPercent: 0,
        trend: "stable",
        status: "green",
      },
      margin: { budgetedMargin: 0, actualMargin: 0, variance: 0, status: "green" },
      hours: { budget: 0, actual: 0, remaining: 0, progressPercent: 0, status: "green" },
      health: { score: "green", statusText: "On Track", factors: [] },
    },
    costBudget: {
      projectId: numericProjectId,
      categories: [],
      phases: [],
      totals: { budget: 0, actual: 0, committed: 0, available: 0, progressPercent: 0 },
    },
    salesBudget: {
      projectId: numericProjectId,
      projectType: (project?.type as "fixed-price" | "time-and-materials" | "retainer") || "fixed-price",
    },
    resourceBudget: {
      projectId: numericProjectId,
      summary: {
        totalBudget: 0,
        totalActual: 0,
        totalRemaining: 0,
        progressPercent: 0,
        billablePercent: 0,
        targetBillablePercent: 80,
        totalBudgetCost: 0,
        totalActualCost: 0,
      },
      byRole: [],
      byPerson: [],
      byPhase: [],
    },
    allBudgets: {
      projectId: numericProjectId,
      profitability: {
        revenue: { budget: 0, actual: 0, variance: 0 },
        directCosts: { budget: 0, actual: 0, variance: 0 },
        grossProfit: { budget: 0, actual: 0, variance: 0 },
        grossMargin: { budget: 0, actual: 0, variance: 0 },
        overhead: { budget: 0, actual: 0, variance: 0 },
        netProfit: { budget: 0, actual: 0, variance: 0 },
        netMargin: { budget: 0, actual: 0, variance: 0 },
      },
      burnRateData: [],
      forecast: {
        projectedFinalRevenue: 0,
        projectedFinalCost: 0,
        projectedFinalMargin: 0,
        revenueVariance: 0,
        costVariance: 0,
        marginVariance: 0,
      },
    },
    alerts: [],
    revisions: [],
    lastUpdated: new Date().toISOString(),
  });

  // Show full-screen edit page if editing cost budget
  if (editCostBudgetPageOpen) {
    const budgetDataToUse = budgetData || getDefaultBudgetData();
    // Override projectName with actual project name/code instead of the placeholder from the API
    const projectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || budgetDataToUse.projectName;
    const budgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: projectDisplayName,
    };
    return (
      <EditCostBudgetPage
        budgetData={budgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        onSave={async (updatedBudget) => {
          console.log("[ProjectDetails] Saving cost budget:", updatedBudget);

          try {
            // Update the budget data
            const result = await updateBudget(project.id, updatedBudget);

            if (result.success) {
              toast.success("Cost budget saved successfully");

              // Close the edit page
              setEditCostBudgetPageOpen(false);

              // Refetch the budget data to show updated values
              refetchBudget();
            } else {
              toast.error("Failed to save cost budget");
            }
          } catch (error) {
            console.error("[ProjectDetails] Error saving budget:", error);
            toast.error("Failed to save cost budget");
          }
        }}
        onCancel={() => setEditCostBudgetPageOpen(false)}
      />
    );
  }

  // Show full-screen edit page if editing sales budget (T&M projects only)
  // Use enrichedBudgetData if available, otherwise use default empty budget data for new projects
  if (editSalesBudgetPageOpen) {
    const budgetDataToUse = enrichedBudgetData || getDefaultBudgetData();
    const projectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || `Project ${projectId}`;
    const budgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: projectDisplayName,
    };
    return (
      <EditSalesBudgetPage
        budgetData={budgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        onSave={async (updatedBudget) => {
          console.log("[ProjectDetails] Saving sales budget:", updatedBudget);

          const result = await updateBudget(project.id, updatedBudget);

          if (result.success) {
            toast.success("Sales budget saved successfully");

            // Close the edit page
            setEditSalesBudgetPageOpen(false);

            // Refetch the budget data to show updated values
            refetchBudget();
          } else {
            throw new Error(result.error || "Failed to save sales budget");
          }
        }}
        onCancel={() => setEditSalesBudgetPageOpen(false)}
      />
    );
  }

  // Show full-screen edit page if editing resource budget
  // Use enrichedBudgetData if available, otherwise use default empty budget data for new projects
  if (editResourceBudgetPageOpen) {
    const budgetDataToUse = enrichedBudgetData || getDefaultBudgetData();
    // Override projectName with actual project name/code instead of the placeholder from the API
    const resourceProjectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || budgetDataToUse.projectName;
    const resourceBudgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: resourceProjectDisplayName,
    };
    return (
      <EditResourceBudgetPage
        budgetData={resourceBudgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        onSave={async (updatedBudget) => {
          console.log("[ProjectDetails] Saving resource budget:", updatedBudget);

          try {
            // Update the budget data
            const result = await updateBudget(project.id, updatedBudget);

            if (result.success) {
              toast.success("Resource budget saved successfully");

              // Close the edit page
              setEditResourceBudgetPageOpen(false);

              // Refetch the budget data to show updated values
              refetchBudget();
            } else {
              toast.error("Failed to save resource budget");
            }
          } catch (error) {
            console.error("[ProjectDetails] Error saving budget:", error);
            toast.error("Failed to save resource budget");
          }
        }}
        onCancel={() => setEditResourceBudgetPageOpen(false)}
      />
    );
  }

  return (
    <div className="space-y-6" style={{ paddingBottom: isEditMode ? "80px" : "0" }}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (onBack ? onBack() : navigate(ROUTES.PROJECTS))}
            className="h-8 w-8 mt-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <PageHeaderBlock
            title={project.code ? `${project.code} - ${project.name}` : project.name}
            subtitle={project.description || "Project details and management"}
            size={20}
            showActions={false}
          />
        </div>
        <Button variant="outline" onClick={handleEnterEditMode} disabled={isEditMode}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Project
        </Button>
      </div>

      {/* Edit Mode Indicator */}
      {isEditMode && (
        <div className="mb-4 p-3 bg-info-50 border border-info-200 rounded-lg flex items-center gap-2">
          <Edit className="h-4 w-4 text-info-700" />
          <span className="text-sm font-medium text-info-700">Edit Mode Active</span>
          <span className="text-sm text-info-600">Make your changes and click Save when done.</span>
        </div>
      )}

      {/* Tabs */}
      <TooltipProvider>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="financials" className="flex items-center gap-2">
              <Landmark className="h-4 w-4" />
              Financials
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Billing
            </TabsTrigger>
            {isBudgetingEnabled && budgetPermissions.settings.permissions.salesBudgetEnabled && (
              <TabsTrigger value="sales-budget" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Sales Budget
              </TabsTrigger>
            )}
            {isBudgetingEnabled && budgetPermissions.settings.permissions.costBudgetEnabled && (
              <TabsTrigger value="cost-budget" className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />
                Cost Budget
              </TabsTrigger>
            )}
            {isBudgetingEnabled && isResourceBudgetEnabled && (
              <TabsTrigger value="resource-budget" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Resource Budget
              </TabsTrigger>
            )}
            <TabsTrigger value="cost-centres" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Cost Centres
            </TabsTrigger>
            <TabsTrigger value="purchases" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Purchases
            </TabsTrigger>
            <TabsTrigger value="time" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Time
            </TabsTrigger>
            <TabsTrigger value="expenses" className="flex items-center gap-2">
              <Banknote className="h-4 w-4" />
              Expenses
            </TabsTrigger>
            <TabsTrigger value="team" className="flex items-center gap-2">
              <UsersRound className="h-4 w-4" />
              Team
            </TabsTrigger>
            <TabsTrigger value="milestones" className="flex items-center gap-2">
              <MilestoneIcon className="h-4 w-4" />
              Milestones
            </TabsTrigger>
            <TabsTrigger value="changes" className="flex items-center gap-2">
              <GitPullRequestArrow className="h-4 w-4" />
              Change Requests
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Audit
            </TabsTrigger>
          </TabsList>

          {/* Scrollable Tab Content Container */}
          <div ref={tabsContainerRef}>
            <ScrollableTabsContainer height={`${tabsContainerHeight}px`}>
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                {/* Key Metrics - Collapsible */}
                <Collapsible open={isKpiOpen} onOpenChange={setIsKpiOpen}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Key Metrics</h2>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="w-9 p-0">
                        {isKpiOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        <span className="sr-only">Toggle KPI cards</span>
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-4">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Revenue Budget</CardTitle>
                          <Coins className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-2xl font-bold truncate">
                                <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p className="text-sm">{formatCurrency(project.budgetRevenue)}</p>
                            </TooltipContent>
                          </Tooltip>
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            Actual: <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            Cost Budget:{" "}
                            <SensitiveData>
                              {formatCurrency(project.budgetLabour + project.budgetExpense)}
                            </SensitiveData>
                          </p>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Cost Variance</CardTitle>
                          {project.variancePercent < 0 ? (
                            <TrendingDown className="h-4 w-4 text-error-500" />
                          ) : (
                            <TrendingUp className="h-4 w-4 text-success-500" />
                          )}
                        </CardHeader>
                        <CardContent>
                          <div className={`text-2xl font-bold ${getVarianceColor(project.variancePercent)}`}>
                            {project.variancePercent > 0 ? "+" : ""}
                            {formatPercent(project.variancePercent)}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            <SensitiveData>{formatCurrency(Math.abs(project.variance))}</SensitiveData>{" "}
                            {project.variance < 0 ? "over budget" : "under budget"}
                          </p>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Margin</CardTitle>
                          <BarChart3 className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">
                            <SensitiveData>{formatPercent(project.marginPercent)}</SensitiveData>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Profit:{" "}
                            <SensitiveData>
                              {formatCurrency(project.actualRevenue - project.actualLabour - project.actualExpense)}
                            </SensitiveData>
                          </p>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Hours Budget</CardTitle>
                          <Clock className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">{project.budgetHours.toLocaleString()} hrs</div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Actual: {project.actualHours.toLocaleString()} hrs
                          </p>
                          <p className={`text-xs mt-1 ${getVarianceColor(project.hoursVariancePercent)}`}>
                            {project.hoursVariance > 0 ? "+" : ""}
                            {project.hoursVariance.toLocaleString()} hrs ({project.hoursVariancePercent > 0 ? "+" : ""}
                            {formatPercent(project.hoursVariancePercent)})
                          </p>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Completion</CardTitle>
                          <Activity className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          {/* Task 13.2 — Inline editable % (fixed-price % complete only) */}
                          {project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete' ? (
                            completionPercentEditing ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={completionPercentInputValue}
                                  onChange={(e) => setCompletionPercentInputValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCompletionPercentEditConfirm();
                                    if (e.key === 'Escape') handleCompletionPercentEditCancel();
                                  }}
                                  className="w-20 h-8 text-lg font-bold"
                                  autoFocus
                                />
                                <span className="text-lg font-bold">%</span>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCompletionPercentEditConfirm}>Save</Button>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCompletionPercentEditCancel}>Cancel</Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 group">
                                <div className="text-2xl font-bold">{effectiveCompletionPercent}%</div>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={handleCompletionPercentEditStart}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
                                        aria-label="Edit completion percentage"
                                      >
                                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Override completion %</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            )
                          ) : (
                            <div className="text-2xl font-bold">{formatPercent(project.completionPercent, 0)}</div>
                          )}

                          <Progress value={project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete' ? effectiveCompletionPercent : project.completionPercent} className="mt-2" />

                          {/* Task 13.3 — Mode indicator beneath progress bar */}
                          {project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete' && (
                            <div className="mt-2 space-y-1">
                              {project.completionPercentMode === 'based-on-time' && !project.completionPercentOverride ? (
                                <p className="text-xs text-muted-foreground">
                                  Auto · based on {project.actualHours.toLocaleString()} of {project.budgetHours.toLocaleString()} approved hours
                                </p>
                              ) : project.completionPercentMode === 'based-on-time' && project.completionPercentOverride !== undefined ? (
                                <div className="space-y-1">
                                  <p className="text-xs text-muted-foreground">
                                    Manual override · set by {project.completionPercentOverriddenBy ?? 'PM'}{' '}
                                    on {project.completionPercentOverriddenAt ? new Date(project.completionPercentOverriddenAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Approved hours suggest {autoCalculatedCompletionPercent}%{' '}
                                    <button
                                      className="text-primary underline underline-offset-2 hover:no-underline"
                                      onClick={() => handleCompletionModeChange('based-on-time')}
                                    >
                                      Apply
                                    </button>
                                  </p>
                                </div>
                              ) : (
                                // manual mode
                                <p className="text-xs text-muted-foreground">
                                  Manual · last updated by {project.completionPercentOverriddenBy ?? 'PM'}{' '}
                                  on {project.completionPercentOverriddenAt ? new Date(project.completionPercentOverriddenAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Phase 17 — Navigation link to Audit tab (replaces inline audit collapsible from Phase 13.4) */}
                          {project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete' && completionAuditEntries.length > 0 && (
                            <button
                              className="mt-3 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => {
                                setAuditCategoryFilter('completion');
                                setActiveTab('audit');
                              }}
                            >
                              <History className="h-3.5 w-3.5" />
                              View audit history →
                            </button>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <div className="grid gap-4 md:grid-cols-2">
                  {/* Project Information */}
                  <Card className={isEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                    <CardHeader>
                      <CardTitle>Project Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {isEditMode ? (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="edit-name">Project Name</Label>
                            <Input
                              id="edit-name"
                              value={editFormData.name || ""}
                              onChange={(e) => handleFieldChange("name", e.target.value)}
                            />
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label htmlFor="edit-description">Description</Label>
                            <Textarea
                              id="edit-description"
                              value={editFormData.description || ""}
                              onChange={(e) => handleFieldChange("description", e.target.value)}
                              rows={3}
                            />
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label htmlFor="edit-status">Status</Label>
                            <Select
                              value={String(editFormData.statusId || project.statusId)}
                              onValueChange={(value) => handleFieldChange("statusId", Number(value))}
                            >
                              <SelectTrigger id="edit-status">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {selectableStatuses.length > 0
                                  ? selectableStatuses.map((s) => (
                                      <SelectItem key={s.id} value={String(s.id)}>
                                        {s.name}
                                      </SelectItem>
                                    ))
                                  : Object.entries(PROJECT_STATUS_LABELS).map(([id, label]) => (
                                      <SelectItem key={id} value={id}>
                                        {label}
                                      </SelectItem>
                                    ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label htmlFor="edit-type">Project Type</Label>
                            <Select
                              value={editFormData.type || project.type}
                              onValueChange={(value) => handleFieldChange("type", value)}
                            >
                              <SelectTrigger id="edit-type">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="fixed-price">Fixed Price</SelectItem>
                                <SelectItem value="time-and-materials">Time & Materials</SelectItem>
                                <SelectItem value="retainer">Retainer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label htmlFor="edit-revenue-method">Revenue Recognition</Label>
                            <Select
                              value={editFormData.revenueRecognitionMethod || project.revenueRecognitionMethod}
                              onValueChange={(value) => handleFieldChange("revenueRecognitionMethod", value)}
                            >
                              <SelectTrigger id="edit-revenue-method">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage-of-completion">Percentage of Completion</SelectItem>
                                <SelectItem value="completed-contract">Completed Contract</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label htmlFor="edit-start-date">Start Date</Label>
                            <Input
                              id="edit-start-date"
                              type="date"
                              value={formatDateForInput(editFormData.startDate || project.startDate)}
                              onChange={(e) => handleFieldChange("startDate", e.target.value)}
                            />
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label htmlFor="edit-end-date">End Date</Label>
                            <Input
                              id="edit-end-date"
                              type="date"
                              value={formatDateForInput(editFormData.endDate || project.endDate)}
                              onChange={(e) => handleFieldChange("endDate", e.target.value)}
                            />
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label htmlFor="edit-project-manager">Project Manager</Label>
                            <EmployeeCombobox
                              employees={employees}
                              value={projectManager?.id !== undefined ? String(projectManager.id) : undefined}
                              onValueChange={handleProjectManagerChange}
                              placeholder="Select a project manager..."
                              recentEmployeeIds={recentProjectManagerIds}
                              maxRecentEmployees={5}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Status</span>
                            <Badge variant={project.statusId === 2 ? "default" : "secondary"}>
                              {statusLabels[project.statusId] || PROJECT_STATUS_LABELS[project.statusId] || String(project.statusId)}
                            </Badge>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Project Type</span>
                            <span className="font-medium">{formatProjectType(project.type)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Revenue Recognition</span>
                            <span className="font-medium">
                              {formatRevenueRecognition(project.revenueRecognitionMethod)}
                            </span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Start Date</span>
                            <span className="font-medium">{formatDate(project.startDate)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">End Date</span>
                            <span className="font-medium">{formatDate(project.endDate)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Project Manager</span>
                            <span className="font-medium">{projectManager?.name || "Unknown"}</span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Client Information */}
                  <Card className={cn(isEditMode && "ring-2 ring-primary/20 bg-primary/5")}>
                    <CardHeader>
                      <CardTitle>Client Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {isEditMode ? (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="client-name">Client Name</Label>
                            <ClientCombobox
                              clients={clients}
                              value={client?.id !== undefined ? String(client.id) : undefined}
                              onValueChange={handleClientChange}
                              placeholder="Select a client..."
                              recentClientIds={recentClientIds}
                              maxRecentClients={5}
                            />
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label>Industry</Label>
                            <div className="text-sm text-muted-foreground">
                              {client?.industry || "Select a client to see industry"}
                            </div>
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label>Contact Name</Label>
                            <div className="text-sm text-muted-foreground">
                              {client?.contactName || "Select a client to see contact"}
                            </div>
                          </div>
                          <Separator />
                          <div className="space-y-2">
                            <Label>Contact Email</Label>
                            <div className="text-sm text-muted-foreground">
                              {client?.contactEmail || "Select a client to see email"}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Client Name</span>
                            <span className="font-medium">{client?.name || "Unknown"}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Industry</span>
                            <span className="font-medium">{client?.industry || "Unknown"}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Contact</span>
                            <span className="font-medium">{client?.contactName || "Unknown"}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Email</span>
                            <span className="font-medium text-sm">{client?.contactEmail || "Unknown"}</span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Stats */}
                <div className="grid gap-4 md:grid-cols-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Active Resources</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{activeResources}</div>
                      <p className="text-xs text-muted-foreground">Team members</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Milestones</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {completedMilestones}/{milestones.length}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {overdueMilestones > 0 && `${overdueMilestones} overdue`}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Change Requests</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{changeRequests.length}</div>
                      <p className="text-xs text-muted-foreground">
                        {pendingChangeRequests} pending, {approvedChangeRequests} approved
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">WIP Amount</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        <SensitiveData>{formatCurrency(project.wipAmount)}</SensitiveData>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Unbilled: <SensitiveData>{formatCurrency(project.unbilledAmount)}</SensitiveData>
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Financials Tab */}
              <TabsContent value="financials" className="space-y-4">
                {/* Enhanced Financial Summary Cards */}
                <div className="grid gap-4 md:grid-cols-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Budget: <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                      </p>
                      <div className="flex items-center gap-1 mt-2">
                        {project.actualRevenue >= project.budgetRevenue ? (
                          <TrendingUp className="h-3 w-3 text-success-500" />
                        ) : (
                          <TrendingDown className="h-3 w-3 text-error-500" />
                        )}
                        <span
                          className={`text-xs ${project.actualRevenue >= project.budgetRevenue ? "text-success-600" : "text-error-600"}`}
                        >
                          {formatPercent(
                            ((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100,
                          )}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Total Costs</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        <SensitiveData>{formatCurrency(project.totalActual)}</SensitiveData>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Budget:{" "}
                        <SensitiveData>{formatCurrency(project.budgetLabour + project.budgetExpense)}</SensitiveData>
                      </p>
                      <div className="flex items-center gap-1 mt-2">
                        {project.totalActual <= project.budgetLabour + project.budgetExpense ? (
                          <TrendingDown className="h-3 w-3 text-success-500" />
                        ) : (
                          <TrendingUp className="h-3 w-3 text-error-500" />
                        )}
                        <span
                          className={`text-xs ${project.totalActual <= project.budgetLabour + project.budgetExpense ? "text-success-600" : "text-error-600"}`}
                        >
                          {formatPercent(
                            ((project.totalActual - (project.budgetLabour + project.budgetExpense)) /
                              (project.budgetLabour + project.budgetExpense)) *
                              100,
                          )}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Gross Profit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        <SensitiveData>{formatCurrency(project.actualRevenue - project.totalActual)}</SensitiveData>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Margin: <SensitiveData>{formatPercent(project.marginPercent)}</SensitiveData>
                      </p>
                      <div className="flex items-center gap-1 mt-2">
                        {project.marginPercent >= 30 ? (
                          <>
                            <CheckCircle className="h-3 w-3 text-success-500" />
                            <span className="text-xs text-success-600">Healthy</span>
                          </>
                        ) : project.marginPercent >= 15 ? (
                          <>
                            <AlertCircle className="h-3 w-3 text-warning-500" />
                            <span className="text-xs text-warning-600">At Risk</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-3 w-3 text-error-500" />
                            <span className="text-xs text-error-600">Critical</span>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Billing & WIP</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Billed to Date */}
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Billed to Date</span>
                          <span className="text-sm font-semibold">
                            <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-xs text-muted-foreground">
                            of {formatCurrency(project.budgetRevenue)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatPercent((project.actualRevenue / project.budgetRevenue) * 100)}
                          </span>
                        </div>
                      </div>

                      <Separator />

                      {/* WIP & Unbilled */}
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">WIP (Unbilled)</span>
                          <span className="text-sm font-semibold text-warning-600">
                            <SensitiveData>{formatCurrency(project.wipAmount)}</SensitiveData>
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-xs text-muted-foreground">Ready to invoice</span>
                          <span className="text-xs font-medium text-success-600">
                            <SensitiveData>{formatCurrency(project.unbilledAmount)}</SensitiveData>
                          </span>
                        </div>
                      </div>

                      <Separator />

                      {/* Status Indicator */}
                      <div className="flex items-center gap-1">
                        {(() => {
                          const billingPercent = (project.actualRevenue / project.budgetRevenue) * 100;
                          const completionPercent = project.completionPercent || 0;
                          const billingGap = billingPercent - completionPercent;

                          // Check WIP aging (simulate: if WIP > £50k, assume some is aged)
                          const hasAgedWIP = project.wipAmount > 50000;

                          if (hasAgedWIP) {
                            return (
                              <>
                                <Clock className="h-3 w-3 text-error-500" />
                                <span className="text-xs text-error-600">WIP aging - invoice now</span>
                              </>
                            );
                          } else if (billingGap < -10) {
                            return (
                              <>
                                <AlertCircle className="h-3 w-3 text-error-500" />
                                <span className="text-xs text-error-600">
                                  Under-billing by {Math.abs(billingGap).toFixed(0)}%
                                </span>
                              </>
                            );
                          } else if (billingGap < -5) {
                            return (
                              <>
                                <AlertCircle className="h-3 w-3 text-warning-500" />
                                <span className="text-xs text-warning-600">Slightly under-billing</span>
                              </>
                            );
                          } else if (project.wipAmount > 30000) {
                            return (
                              <>
                                <AlertCircle className="h-3 w-3 text-warning-500" />
                                <span className="text-xs text-warning-600">High WIP - review billing</span>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <CheckCircle className="h-3 w-3 text-success-500" />
                                <span className="text-xs text-success-600">Billing on track</span>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Visual Charts Section */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Profitability Waterfall Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Profitability Breakdown</CardTitle>
                      <CardDescription>Revenue to profit waterfall</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsBarChart
                            data={[
                              { name: "Revenue", value: project.actualRevenue, fill: "#22c55e" },
                              { name: "Labour Costs", value: -project.actualLabour, fill: "#f97316" },
                              { name: "Expenses", value: -project.actualExpense, fill: "#3b82f6" },
                              {
                                name: "Gross Profit",
                                value: project.actualRevenue - project.totalActual,
                                fill: project.actualRevenue - project.totalActual > 0 ? "#22c55e" : "#f97316",
                              },
                            ]}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis dataKey="name" className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                            <YAxis
                              className="text-xs"
                              tick={{ fill: "hsl(var(--muted-foreground))" }}
                              tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                            />
                            <RechartsTooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  return (
                                    <div className="rounded-lg border bg-background p-2 shadow-sm">
                                      <div className="grid gap-2">
                                        <div className="flex flex-col">
                                          <span className="text-[0.70rem] uppercase text-muted-foreground">
                                            {payload[0].payload.name}
                                          </span>
                                          <span className="font-bold text-muted-foreground">
                                            {formatCurrency(Math.abs(payload[0].value as number))}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                              {[
                                { name: "Revenue", value: project.actualRevenue, fill: "#22c55e" },
                                { name: "Labour Costs", value: -project.actualLabour, fill: "#f97316" },
                                { name: "Expenses", value: -project.actualExpense, fill: "#3b82f6" },
                                {
                                  name: "Gross Profit",
                                  value: project.actualRevenue - project.totalActual,
                                  fill: project.actualRevenue - project.totalActual > 0 ? "#22c55e" : "#f97316",
                                },
                              ].map((entry, index) => (
                                <RechartsCell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Bar>
                          </RechartsBarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Margin Trend Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Margin Trend</CardTitle>
                      <CardDescription>6-month margin performance</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsLineChart
                            data={(() => {
                              const months: { month: string; margin: number; target: number }[] = [];
                              const currentMargin = project.marginPercent;
                              const startMargin = currentMargin - 3 + Math.random() * 2;

                              for (let i = 5; i >= 0; i--) {
                                const date = new Date();
                                date.setMonth(date.getMonth() - i);
                                const monthName = date.toLocaleDateString("en-GB", { month: "short" });

                                let margin;
                                if (i === 0) {
                                  margin = currentMargin;
                                } else {
                                  const progress = (5 - i) / 5;
                                  margin =
                                    startMargin + (currentMargin - startMargin) * progress + (Math.random() - 0.5) * 2;
                                }

                                months.push({
                                  month: monthName,
                                  margin: Math.max(0, margin),
                                  target: 30,
                                });
                              }
                              return months;
                            })()}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis
                              dataKey="month"
                              className="text-xs"
                              tick={{ fill: "hsl(var(--muted-foreground))" }}
                            />
                            <YAxis
                              className="text-xs"
                              tick={{ fill: "hsl(var(--muted-foreground))" }}
                              tickFormatter={(value) => `${value}%`}
                            />
                            <RechartsTooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  return (
                                    <div className="rounded-lg border bg-background p-2 shadow-sm">
                                      <div className="grid gap-2">
                                        <div className="flex flex-col">
                                          <span className="text-[0.70rem] uppercase text-muted-foreground">
                                            {payload[0].payload.month}
                                          </span>
                                          <span className="font-bold text-muted-foreground">
                                            Margin: {formatPercent(payload[0].value as number)}
                                          </span>
                                          <span className="text-xs text-muted-foreground">
                                            Target: {formatPercent(payload[0].payload.target)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="target"
                              stroke="hsl(var(--muted-foreground))"
                              strokeDasharray="5 5"
                              strokeWidth={1}
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="margin"
                              stroke="hsl(var(--primary))"
                              strokeWidth={2}
                              dot={{ fill: "hsl(var(--primary))", r: 4 }}
                              activeDot={{ r: 6 }}
                            />
                          </RechartsLineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Cost Breakdown and Budget vs Actual */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Cost Breakdown Pie Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Cost Breakdown</CardTitle>
                      <CardDescription>Distribution of project costs</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={[
                                { name: "Labour", value: project.actualLabour, fill: "#f97316" },
                                { name: "Expenses", value: project.actualExpense, fill: "#3b82f6" },
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                              outerRadius={80}
                              dataKey="value"
                            >
                              {[
                                { name: "Labour", value: project.actualLabour, fill: "#f97316" },
                                { name: "Expenses", value: project.actualExpense, fill: "#3b82f6" },
                              ].map((entry, index) => (
                                <RechartsCell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Pie>
                            <RechartsTooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  return (
                                    <div className="rounded-lg border bg-background p-2 shadow-sm">
                                      <div className="grid gap-2">
                                        <div className="flex flex-col">
                                          <span className="text-[0.70rem] uppercase text-muted-foreground">
                                            {payload[0].name}
                                          </span>
                                          <span className="font-bold text-muted-foreground">
                                            {formatCurrency(payload[0].value as number)}
                                          </span>
                                          <span className="text-xs text-muted-foreground">
                                            {formatPercent(((payload[0].value as number) / project.totalActual) * 100)}{" "}
                                            of total
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full" style={{ backgroundColor: "hsl(var(--chart-1))" }} />
                            <span>Labour</span>
                          </div>
                          <span className="font-medium">
                            <SensitiveData>{formatCurrency(project.actualLabour)}</SensitiveData>
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full" style={{ backgroundColor: "hsl(var(--chart-2))" }} />
                            <span>Expenses</span>
                          </div>
                          <span className="font-medium">
                            <SensitiveData>{formatCurrency(project.actualExpense)}</SensitiveData>
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Budget Burndown Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Budget Burndown</CardTitle>
                      <CardDescription>Budget consumption vs planned burn rate</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsLineChart
                            data={(() => {
                              // Calculate burndown data
                              const totalBudget = project.budgetLabour + project.budgetExpense;
                              const startDate = new Date(project.startDate);
                              const endDate = new Date(project.endDate);
                              const today = new Date();

                              // Calculate project duration in days
                              const totalDays = Math.ceil(
                                (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
                              );
                              const daysElapsed = Math.ceil(
                                (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
                              );

                              // Generate monthly data points
                              const months: { month: string; planned: number; actual: number; budget: number }[] = [];
                              const monthNames = [
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "May",
                                "Jun",
                                "Jul",
                                "Aug",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dec",
                              ];

                              for (let i = 0; i <= 6; i++) {
                                const date = new Date(startDate);
                                date.setMonth(date.getMonth() + i);

                                const monthDaysElapsed = Math.ceil(
                                  (date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
                                );
                                const plannedBurn = totalBudget * (monthDaysElapsed / totalDays);

                                // Actual burn is slightly higher (simulating overrun)
                                const actualBurn = i === 0 ? 0 : plannedBurn * 1.15; // 15% over planned

                                months.push({
                                  month: monthNames[date.getMonth()],
                                  planned: Math.min(plannedBurn, totalBudget),
                                  actual: Math.min(actualBurn, project.totalActual),
                                  budget: totalBudget,
                                });
                              }

                              return months;
                            })()}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis
                              dataKey="month"
                              className="text-xs"
                              tick={{ fill: "hsl(var(--muted-foreground))" }}
                            />
                            <YAxis
                              className="text-xs"
                              tick={{ fill: "hsl(var(--muted-foreground))" }}
                              tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                            />
                            <RechartsTooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  return (
                                    <div className="rounded-lg border bg-background p-2 shadow-sm">
                                      <div className="grid gap-2">
                                        <div className="flex flex-col">
                                          <span className="text-[0.70rem] uppercase text-muted-foreground">
                                            {payload[0].payload.month}
                                          </span>
                                          <span className="font-bold text-success-600">
                                            Planned: {formatCurrency(payload[0].payload.planned)}
                                          </span>
                                          <span className="font-bold text-error-600">
                                            Actual: {formatCurrency(payload[0].payload.actual)}
                                          </span>
                                          <span className="text-xs text-muted-foreground">
                                            Budget: {formatCurrency(payload[0].payload.budget)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <ReferenceLine
                              y={project.budgetLabour + project.budgetExpense}
                              stroke="hsl(var(--muted-foreground))"
                              strokeDasharray="3 3"
                              label={{ value: "Budget", position: "right", fill: "hsl(var(--muted-foreground))" }}
                            />
                            <Line
                              type="monotone"
                              dataKey="planned"
                              stroke="#22c55e"
                              strokeWidth={2}
                              dot={{ fill: "#22c55e", r: 3 }}
                              name="Planned Burn"
                            />
                            <Line
                              type="monotone"
                              dataKey="actual"
                              stroke="#f97316"
                              strokeWidth={2}
                              dot={{ fill: "#f97316", r: 3 }}
                              name="Actual Burn"
                            />
                          </RechartsLineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Budget vs Actual Table */}
                <div className="grid gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Budget vs Actual</CardTitle>
                      <CardDescription>Detailed variance analysis</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Category</TableHead>
                            <TableHead className="text-right">Budget</TableHead>
                            <TableHead className="text-right">Actual</TableHead>
                            <TableHead className="text-right">Variance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-medium">Revenue</TableCell>
                            <TableCell className="text-right">
                              <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                            </TableCell>
                            <TableCell className="text-right">
                              <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                            </TableCell>
                            <TableCell className="text-right">
                              <span
                                className={getVarianceColor(
                                  ((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100,
                                )}
                              >
                                <SensitiveData>
                                  {formatCurrency(project.actualRevenue - project.budgetRevenue)}
                                </SensitiveData>
                              </span>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Labour</TableCell>
                            <TableCell className="text-right">
                              <SensitiveData>{formatCurrency(project.budgetLabour)}</SensitiveData>
                            </TableCell>
                            <TableCell className="text-right">
                              <SensitiveData>{formatCurrency(project.actualLabour)}</SensitiveData>
                            </TableCell>
                            <TableCell className="text-right">
                              <span
                                className={getVarianceColor(
                                  ((project.actualLabour - project.budgetLabour) / project.budgetLabour) * 100,
                                )}
                              >
                                <SensitiveData>
                                  {formatCurrency(project.actualLabour - project.budgetLabour)}
                                </SensitiveData>
                              </span>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Expenses</TableCell>
                            <TableCell className="text-right">
                              <SensitiveData>{formatCurrency(project.budgetExpense)}</SensitiveData>
                            </TableCell>
                            <TableCell className="text-right">
                              <SensitiveData>{formatCurrency(project.actualExpense)}</SensitiveData>
                            </TableCell>
                            <TableCell className="text-right">
                              <span
                                className={getVarianceColor(
                                  ((project.actualExpense - project.budgetExpense) / project.budgetExpense) * 100,
                                )}
                              >
                                <SensitiveData>
                                  {formatCurrency(project.actualExpense - project.budgetExpense)}
                                </SensitiveData>
                              </span>
                            </TableCell>
                          </TableRow>
                          <TableRow className="border-t-2">
                            <TableCell className="font-bold">Total Costs</TableCell>
                            <TableCell className="text-right font-bold">
                              <SensitiveData>
                                {formatCurrency(project.budgetLabour + project.budgetExpense)}
                              </SensitiveData>
                            </TableCell>
                            <TableCell className="text-right font-bold">
                              <SensitiveData>{formatCurrency(project.totalActual)}</SensitiveData>
                            </TableCell>
                            <TableCell className="text-right font-bold">
                              <span className={getVarianceColor(project.variancePercent)}>
                                <SensitiveData>{formatCurrency(project.variance)}</SensitiveData>
                              </span>
                            </TableCell>
                          </TableRow>
                          <TableRow className="border-t-2">
                            <TableCell className="font-bold">Gross Profit</TableCell>
                            <TableCell className="text-right font-bold">
                              <SensitiveData>
                                {formatCurrency(project.budgetRevenue - (project.budgetLabour + project.budgetExpense))}
                              </SensitiveData>
                            </TableCell>
                            <TableCell className="text-right font-bold">
                              <SensitiveData>
                                {formatCurrency(project.actualRevenue - project.totalActual)}
                              </SensitiveData>
                            </TableCell>
                            <TableCell className="text-right font-bold">
                              <span
                                className={getVarianceColor(
                                  ((project.actualRevenue -
                                    project.totalActual -
                                    (project.budgetRevenue - (project.budgetLabour + project.budgetExpense))) /
                                    (project.budgetRevenue - (project.budgetLabour + project.budgetExpense))) *
                                    100,
                                )}
                              >
                                <SensitiveData>
                                  {formatCurrency(
                                    project.actualRevenue -
                                      project.totalActual -
                                      (project.budgetRevenue - (project.budgetLabour + project.budgetExpense)),
                                  )}
                                </SensitiveData>
                              </span>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Billing Tab */}
              <TabsContent value="billing" className="space-y-4">
                {project?.type === "fixed-price" ? (
                  (() => {
                    // Build BillingMilestone[] — prefer the Sales Budget milestones from
                    // enrichedBudgetData because they carry triggerPercent values (needed for
                    // % complete milestone status derivation in Phase 9). Fall back to the raw
                    // project milestones state only when no Sales Budget data is available.
                    const salesBudgetMilestones = enrichedBudgetData?.salesBudget?.milestones;
                    const billingMilestones: import("@/types/budget.types").BillingMilestone[] =
                      salesBudgetMilestones && salesBudgetMilestones.length > 0
                        ? salesBudgetMilestones
                        : milestones.map((m) => ({
                            id: m.id,
                            name: m.name,
                            amount: m.paymentAmount ?? 0,
                            dueDate: m.dueDate,
                            status: (
                              m.status === "completed"
                                ? "paid"
                                : m.status === "overdue"
                                ? "overdue"
                                : new Date(m.dueDate) < new Date()
                                ? "due"
                                : "pending"
                            ) as import("@/types/budget.types").BillingMilestoneStatus,
                          }));

                    // Use the shared fixedFeeInvoices computed at component level (Phase 16).
                    // This ensures the Sales Budget Revenue Health panel and the Billing Tab
                    // both reference the same invoice data without diverging.

                    const contractValue =
                      enrichedBudgetData?.salesBudget?.contractValue ??
                      project.budgetRevenue ??
                      0;

                    return (
                      <FixedFeeBillingTab
                        contractValue={contractValue}
                        milestones={billingMilestones}
                        invoices={fixedFeeInvoices}
                        projectName={project.name}
                        completionPercent={effectiveCompletionPercent}
                        billingTriggerType={project.billingTriggerType}
                        formatCurrency={formatCurrency}
                        onSettingsClick={() => setBillingSettingsDrawerOpen(true)}
                        onGenerateInvoice={(milestone) => {
                          setFixedFeeMilestone({
                            id: milestone.id,
                            name: milestone.name,
                            amount: milestone.amount,
                            dueDate: milestone.dueDate,
                          });
                          setInvoiceWizardOpen(true);
                        }}
                      />
                    );
                  })()
                ) : project?.type !== "time-and-materials" ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <div className="rounded-full bg-muted p-3 mb-4">
                        <Receipt className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
                      <p className="text-sm text-muted-foreground text-center max-w-md">
                        Billing features for {formatProjectType(project?.type || "")} projects are currently under
                        development.
                        <br />
                        Time & Materials project billing is available now.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {/* Billing Sub-tabs */}
                    <Tabs defaultValue="invoices" className="w-full">
                      <TabsList className="grid w-full max-w-md grid-cols-2">
                        <TabsTrigger value="invoices">Invoices</TabsTrigger>
                        <TabsTrigger value="unbilled">Unbilled Work</TabsTrigger>
                      </TabsList>

                      {/* Invoices Tab Content */}
                      <TabsContent value="invoices" className="space-y-4 mt-4">
                        {/* Billing Summary - One Line */}
                        {(() => {
                          // Compute Invoiced from posted/paid invoices (exclude Draft=0 and Void=5)
                          const invoicedTotal = billingInvoices
                            .filter((inv) => inv.status !== 0 && inv.status !== 5)
                            .reduce((sum, inv) => sum + inv.subtotal, 0);
                          const wipTotal = wipSummary.totalNetAmount;
                          const readyToBill = wipTotal;
                          const budgetRevenue = project.budgetRevenue;
                          const billingPercent = budgetRevenue > 0 ? (invoicedTotal / budgetRevenue) * 100 : 0;
                          const completionPercent = project.completionPercent || 0;
                          const billingGap = billingPercent - completionPercent;
                          return (
                        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                          <div className="flex items-center gap-8 flex-1">
                            <div>
                              <span className="text-sm text-neutral-600">Budget:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(budgetRevenue)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Invoiced:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(invoicedTotal)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">WIP:</span>
                              <span className="ml-2 text-base font-semibold text-warning-600">
                                <SensitiveData>{formatCurrency(wipTotal)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Ready to Bill:</span>
                              <span className="ml-2 text-base font-semibold text-success-600">
                                <SensitiveData>{formatCurrency(readyToBill)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Status:</span>
                              <span className="ml-2">
                                {billingLoading ? (
                                  <Badge variant="outline">Loading...</Badge>
                                ) : billingGap < -10 ? (
                                  <Badge variant="destructive">Under-billing</Badge>
                                ) : billingGap < -5 ? (
                                  <Badge variant="warning">Review</Badge>
                                ) : (
                                  <Badge variant="success">On track</Badge>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                          );
                        })()}

                        {/* Invoice History Table */}
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle>Invoice History</CardTitle>
                                <CardDescription>All invoices raised for this project</CardDescription>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => setBillingSettingsDrawerOpen(true)}>
                                  <Settings2 className="h-4 w-4 mr-2" />
                                  Billing Settings
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setInvoiceColumnConfigOpen(true)}>
                                  <Settings2 className="h-4 w-4 mr-2" />
                                  Configure
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {/* Filters and Search */}
                            <div className="flex items-center gap-4 mb-4">
                              <div className="relative flex-1 max-w-sm">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  placeholder="Search invoices..."
                                  value={invoiceSearchQuery}
                                  onChange={(e) => {
                                    setInvoiceSearchQuery(e.target.value);
                                    setInvoiceCurrentPage(1); // Reset to first page when searching
                                  }}
                                  className="pl-9 pr-9"
                                />
                                {invoiceSearchQuery && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                                    onClick={() => {
                                      setInvoiceSearchQuery("");
                                      setInvoiceCurrentPage(1);
                                    }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                              
                              <Select
                                value={invoiceStatusFilter}
                                onValueChange={(value) => {
                                  setInvoiceStatusFilter(value);
                                  setInvoiceCurrentPage(1); // Reset to first page when filtering
                                }}
                              >
                                <SelectTrigger id="status-filter" className="w-[180px]">
                                  <SelectValue placeholder="All Statuses" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Statuses</SelectItem>
                                  <SelectItem value="Draft">Draft</SelectItem>
                                  <SelectItem value="Sent">Sent</SelectItem>
                                  <SelectItem value="Paid">Paid</SelectItem>
                                  <SelectItem value="Overdue">Overdue</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {(() => {
                              // Invoice data - uses API data in live mode, mock data in demo mode
                              const allInvoices = (dataMode === "live")
                                ? billingInvoices.map((inv) => ({
                                    id: inv.id,
                                    invoiceNumber: inv.invoiceNumber || null,
                                    date: inv.invoiceDate ? inv.invoiceDate.split("T")[0] : null,
                                    description: inv.description || "",
                                    amount: inv.grossTotal,
                                    created: inv.createdBy ? inv.invoiceDate?.split("T")[0] || null : null,
                                    due: inv.dueDate ? inv.dueDate.split("T")[0] : null,
                                    status: mapInvoiceStatus(inv.status) as "Draft" | "Sent" | "Paid" | "Overdue" | "PartiallyPaid" | "Void" | "WriteOff",
                                  }))
                                : [
                                {
                                  id: 1,
                                  invoiceNumber: "INV-2024-001" as string | null,
                                  date: "2024-01-15" as string | null,
                                  description: "Monthly Development Services - January",
                                  amount: 12500,
                                  created: "2024-01-10" as string | null,
                                  due: "2024-02-15" as string | null,
                                  status: "Paid" as const,
                                },
                                {
                                  id: 2,
                                  invoiceNumber: "INV-2024-002" as string | null,
                                  date: "2024-02-15" as string | null,
                                  description: "Monthly Development Services - February",
                                  amount: 15000,
                                  created: "2024-02-10" as string | null,
                                  due: "2024-03-15" as string | null,
                                  status: "Paid" as const,
                                },
                                {
                                  id: 3,
                                  invoiceNumber: "INV-2024-003" as string | null,
                                  date: "2024-03-15" as string | null,
                                  description: "Monthly Development Services - March",
                                  amount: 13750,
                                  created: "2024-03-10" as string | null,
                                  due: "2024-04-15" as string | null,
                                  status: "Overdue" as const,
                                },
                                {
                                  id: 4,
                                  invoiceNumber: "INV-2024-004" as string | null,
                                  date: "2024-04-15" as string | null,
                                  description: "Monthly Development Services - April",
                                  amount: 14200,
                                  created: "2024-04-10" as string | null,
                                  due: "2024-05-15" as string | null,
                                  status: "Sent" as const,
                                },
                                {
                                  id: 5,
                                  invoiceNumber: "DRAFT-005" as string | null,
                                  date: null as string | null,
                                  description: "Monthly Development Services - May",
                                  amount: 8500,
                                  created: "2024-05-01" as string | null,
                                  due: null as string | null,
                                  status: "Draft" as const,
                                },
                                {
                                  id: 6,
                                  invoiceNumber: null as string | null,
                                  date: null as string | null,
                                  description: "Additional Features Development",
                                  amount: 5200,
                                  created: null as string | null,
                                  due: null as string | null,
                                  status: "Draft" as const,
                                },
                              ];

                              // Apply filters and search
                              const filteredInvoices = allInvoices.filter((invoice) => {
                                // Status filter
                                const matchesStatus = invoiceStatusFilter === "all" || invoice.status === invoiceStatusFilter;
                                
                                // Search filter (searches invoice number, description)
                                const searchLower = invoiceSearchQuery.toLowerCase();
                                const matchesSearch = 
                                  invoiceSearchQuery === "" ||
                                  (invoice.invoiceNumber?.toLowerCase().includes(searchLower)) ||
                                  invoice.description.toLowerCase().includes(searchLower);
                                
                                return matchesStatus && matchesSearch;
                              });

                              const invoices = filteredInvoices;

                              const getStatusBadge = (status: string) => {
                                switch (status) {
                                  case "Paid":
                                    return <Badge variant="success">Paid</Badge>;
                                  case "Sent":
                                    return <Badge variant="default">Sent</Badge>;
                                  case "Overdue":
                                    return <Badge variant="destructive">Overdue</Badge>;
                                  case "Draft":
                                    return <Badge variant="secondary">Draft</Badge>;
                                  default:
                                    return <Badge variant="outline">{status}</Badge>;
                                }
                              };

                              // Pagination logic
                              const invoiceStartIndex = (invoiceCurrentPage - 1) * invoiceItemsPerPage;
                              const invoiceEndIndex = invoiceStartIndex + invoiceItemsPerPage;
                              const paginatedInvoices = invoices.slice(invoiceStartIndex, invoiceEndIndex);
                              const invoiceTotalPages = Math.ceil(invoices.length / invoiceItemsPerPage);
                              const invoiceStartItem = invoices.length === 0 ? 0 : invoiceStartIndex + 1;
                              const invoiceEndItem = Math.min(invoiceEndIndex, invoices.length);

                              // Calculate totals
                              const totalInvoiced = invoices.reduce((sum, inv) => sum + inv.amount, 0);
                              const outstanding = invoices
                                .filter((inv) => ["Sent", "Overdue"].includes(inv.status))
                                .reduce((sum, inv) => sum + inv.amount, 0);
                              const paid = invoices
                                .filter((inv) => inv.status === "Paid")
                                .reduce((sum, inv) => sum + inv.amount, 0);

                              return (
                                <>
                                  <div className="rounded-md border">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          {visibleInvoiceColumns.map((column) => (
                                            <TableHead 
                                              key={column.id}
                                              className={column.align === 'right' ? 'text-right' : ''}
                                            >
                                              {column.name}
                                            </TableHead>
                                          ))}
                                          <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {invoices.length === 0 ? (
                                          <TableRow>
                                            <TableCell 
                                              colSpan={visibleInvoiceColumns.length + 1} 
                                              className="text-center py-12 text-muted-foreground"
                                            >
                                              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                              <p className="text-sm">No invoice data available</p>
                                              <p className="text-xs mt-2">
                                                Invoice history will appear here once integrated with your billing
                                                system
                                              </p>
                                            </TableCell>
                                          </TableRow>
                                        ) : (
                                          paginatedInvoices.map((invoice) => {
                                            // Helper function to render cell content based on column ID
                                            const renderCell = (column: ColumnDefinition) => {
                                              switch (column.id) {
                                                case 'invoice-number':
                                                  return (
                                                    <TableCell key={column.id} className="font-medium">
                                                      {invoice.invoiceNumber || `#${invoice.id}`}
                                                    </TableCell>
                                                  );
                                                case 'date':
                                                  return (
                                                    <TableCell key={column.id}>
                                                      {invoice.date
                                                        ? new Date(invoice.date).toLocaleDateString("en-GB", {
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                          })
                                                        : "-"}
                                                    </TableCell>
                                                  );
                                                case 'description':
                                                  return (
                                                    <TableCell key={column.id}>{invoice.description}</TableCell>
                                                  );
                                                case 'amount':
                                                  return (
                                                    <TableCell key={column.id} className="text-right font-medium">
                                                      <SensitiveData>{formatCurrency(invoice.amount)}</SensitiveData>
                                                    </TableCell>
                                                  );
                                                case 'status':
                                                  return (
                                                    <TableCell key={column.id}>{getStatusBadge(invoice.status)}</TableCell>
                                                  );
                                                default:
                                                  return null;
                                              }
                                            };

                                            return (
                                            <TableRow key={invoice.id}>
                                              {visibleInvoiceColumns.map(renderCell)}
                                              <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                  {invoice.status === "Draft" ? (
                                                    <>
                                                      <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                          setSelectedInvoiceId(invoice.id);
                                                          setInvoiceDetailsSheetOpen(true);
                                                        }}
                                                      >
                                                        <FileText className="h-4 w-4 mr-1" />
                                                        View
                                                      </Button>
                                                      <AlertDialog>
                                                        <AlertDialogTrigger asChild>
                                                          <Button
                                                            size="sm"
                                                            variant="outline"
                                                            disabled={billingActionLoading}
                                                          >
                                                            <Trash2 className="h-4 w-4 mr-1" />
                                                            Delete
                                                          </Button>
                                                        </AlertDialogTrigger>
                                                        <AlertDialogContent>
                                                          <AlertDialogHeader>
                                                            <AlertDialogTitle>Delete Draft Invoice</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                              This will delete the draft invoice and return all associated WIP items back to Unbilled Work. The line items on this draft will be removed.
                                                            </AlertDialogDescription>
                                                          </AlertDialogHeader>
                                                          <AlertDialogFooter>
                                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                            <AlertDialogAction onClick={() => handleDeleteDraft(invoice.id)}>
                                                              Delete &amp; Return to WIP
                                                            </AlertDialogAction>
                                                          </AlertDialogFooter>
                                                        </AlertDialogContent>
                                                      </AlertDialog>
                                                    </>
                                                  ) : (
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      onClick={() => {
                                                        setSelectedInvoiceId(invoice.id);
                                                        setInvoiceDetailsSheetOpen(true);
                                                      }}
                                                    >
                                                      <FileText className="h-4 w-4 mr-1" />
                                                      View
                                                    </Button>
                                                  )}
                                                </div>
                                              </TableCell>
                                            </TableRow>
                                            );
                                          })
                                        )}
                                      </TableBody>
                                    </Table>
                                  </div>

                                  {/* Pagination */}
                                  {invoices.length > 0 && (
                                    <div className="flex items-center justify-between mt-4">
                                      <div className="text-sm text-muted-foreground">
                                        Showing {invoiceStartItem}-{invoiceEndItem} of {invoices.length} invoices
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm text-muted-foreground mr-2">
                                          Page {invoiceCurrentPage} of {invoiceTotalPages || 1}
                                        </span>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          disabled={invoiceCurrentPage === 1}
                                          onClick={() => handleInvoicePageChange(invoiceCurrentPage - 1)}
                                        >
                                          <ChevronLeft className="h-4 w-4" />
                                          Previous
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          disabled={invoiceCurrentPage >= invoiceTotalPages}
                                          onClick={() => handleInvoicePageChange(invoiceCurrentPage + 1)}
                                        >
                                          Next
                                          <ChevronRight className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  )}

                                  {/* Footer with totals */}
                                  {invoices.length > 0 && (
                                    <div className="mt-6 pt-4 border-t">
                                      <div className="grid gap-4 md:grid-cols-3">
                                        <div className="space-y-1">
                                          <div className="text-sm text-muted-foreground">Total Invoiced</div>
                                          <div className="text-xl font-bold">
                                            <SensitiveData>{formatCurrency(totalInvoiced)}</SensitiveData>
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          <div className="text-sm text-muted-foreground">Outstanding</div>
                                          <div className="text-xl font-bold text-warning-600">
                                            <SensitiveData>{formatCurrency(outstanding)}</SensitiveData>
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          <div className="text-sm text-muted-foreground">Paid</div>
                                          <div className="text-xl font-bold text-success-600">
                                            <SensitiveData>{formatCurrency(paid)}</SensitiveData>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </CardContent>
                        </Card>

                        {/* Payment Tracking */}
                        <Card>
                          <CardHeader>
                            <CardTitle>Payment Tracking</CardTitle>
                            <CardDescription>Outstanding and received payments</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-2 p-4 border rounded-lg">
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock className="h-4 w-4" />
                                    Outstanding
                                  </div>
                                  <div className="text-2xl font-bold">
                                    <SensitiveData>{formatCurrency(0)}</SensitiveData>
                                  </div>
                                  <div className="text-xs text-muted-foreground">0 invoices pending payment</div>
                                </div>

                                <div className="space-y-2 p-4 border rounded-lg">
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <CheckCircle className="h-4 w-4" />
                                    Received
                                  </div>
                                  <div className="text-2xl font-bold text-success-600">
                                    <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                                  </div>
                                  <div className="text-xs text-muted-foreground">All invoices paid</div>
                                </div>
                              </div>

                              <Separator />

                              <div className="text-center py-8 text-muted-foreground">
                                <p className="text-sm">Payment tracking details will appear here</p>
                                <p className="text-xs mt-2">Connect your accounting system to track payment status</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>

                      {/* Unbilled Work Tab Content */}
                      <TabsContent value="unbilled" className="space-y-4 mt-4">
                        {/* WIP Summary - One Line (uses server-side summary for accurate totals across all pages) */}
                        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                          <div className="flex items-center gap-8 flex-1">
                            <div>
                              <span className="text-sm text-neutral-600">Time WIP:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(wipSummary.timeNetAmount)}</SensitiveData>
                              </span>
                              <span className="ml-1 text-xs text-neutral-500">({wipSummary.timeHours.toFixed(1)} hrs)</span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Expense WIP:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(wipSummary.expenseNetAmount)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Total WIP:</span>
                              <span className="ml-2 text-base font-semibold text-success-600">
                                <SensitiveData>{formatCurrency(wipSummary.totalNetAmount)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Status:</span>
                              <span className="ml-2">
                                {billingLoading ? (
                                  <Badge variant="outline">Loading...</Badge>
                                ) : wipSummary.totalNetAmount > 0 ? (
                                  <Badge variant="success">Ready to Bill</Badge>
                                ) : (
                                  <Badge variant="secondary">No Items</Badge>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* WIP Items Table */}
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle>Unbilled Work Items</CardTitle>
                                <CardDescription>Select items to include in the next invoice</CardDescription>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setWipColumnConfigOpen(true)}
                                >
                                  <Settings2 className="h-4 w-4 mr-2" />
                                  Configure
                                </Button>
                                {dataMode === "live" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2"
                                    disabled={selectedWipItems.length === 0 || billingActionLoading}
                                    onClick={() => setCreateDraftDialogOpen(true)}
                                  >
                                    <FileText className="h-4 w-4" />
                                    Create Draft
                                  </Button>
                                )}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span tabIndex={!client ? 0 : undefined}>
                                      <Button 
                                        className="gap-2" 
                                        disabled={!client || selectedWipItems.length === 0 || billingActionLoading}
                                        onClick={() => setInvoiceWizardOpen(true)}
                                        style={!client ? { pointerEvents: "none" } : undefined}
                                      >
                                        <FileText className="h-4 w-4" />
                                        Generate Invoice
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {!client && (
                                    <TooltipContent>
                                      <p>Project has no Client set, Please set Client before you can Generate Invoice</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {/* Filters */}
                            <div className="flex flex-wrap gap-4 mb-4">
                              <div className="flex-1 min-w-[200px]">
                                <Input
                                  placeholder="Search by description..."
                                  className="w-full"
                                  value={wipSearchQuery}
                                  onChange={(e) => setWipSearchQuery(e.target.value)}
                                />
                              </div>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="w-[150px] justify-start text-left font-normal">
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {wipDateFrom ? wipDateFrom.toLocaleDateString() : "From Date"}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="single"
                                    selected={wipDateFrom}
                                    onSelect={setWipDateFrom}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="w-[150px] justify-start text-left font-normal">
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {wipDateTo ? wipDateTo.toLocaleDateString() : "To Date"}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="single"
                                    selected={wipDateTo}
                                    onSelect={setWipDateTo}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              {(wipDateFrom || wipDateTo) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setWipDateFrom(undefined);
                                    setWipDateTo(undefined);
                                  }}
                                >
                                  Clear Dates
                                </Button>
                              )}
                              <Select value={wipTypeFilter} onValueChange={setWipTypeFilter}>
                                <SelectTrigger className="w-[150px]">
                                  <SelectValue placeholder="Type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all-types">All Types</SelectItem>
                                  <SelectItem value="time">Time</SelectItem>
                                  <SelectItem value="expense">Expense</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select value={wipResourceFilter} onValueChange={setWipResourceFilter}>
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue placeholder="Resource" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all-resources">All Resources</SelectItem>
                                  {Array.from(new Set(wipItems.map((item) => item.resource)))
                                    .sort()
                                    .map((resource) => (
                                      <SelectItem key={resource} value={resource.toLowerCase()}>
                                        {resource}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <Select value={wipGroupBy} onValueChange={setWipGroupBy}>
                                <SelectTrigger className="w-[150px]">
                                  <SelectValue placeholder="Group By" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  <SelectItem value="resource">Resource</SelectItem>
                                  <SelectItem value="task">Task</SelectItem>
                                  <SelectItem value="date">Date</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Table */}
                            <div className="border rounded-lg">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-[50px]">
                                      <input
                                        type="checkbox"
                                        className="rounded"
                                        checked={
                                          filteredAndSortedWipItems.length > 0 &&
                                          selectedWipItems.length === filteredAndSortedWipItems.length
                                        }
                                        onChange={handleSelectAllWipItems}
                                      />
                                    </TableHead>
                                    {visibleWipColumns.map((column) => (
                                      <TableHead
                                        key={column.id}
                                        className={column.align === "right" ? "text-right" : ""}
                                      >
                                        {(column as any).sortable !== false ? (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 px-2 hover:bg-transparent"
                                            onClick={() => handleWipSort(column.id as any)}
                                          >
                                            {column.name}
                                            {wipSortColumn === column.id && (
                                              wipSortDirection === "asc" ? (
                                                <ChevronUp className="ml-1 h-4 w-4" />
                                              ) : (
                                                <ChevronDown className="ml-1 h-4 w-4" />
                                              )
                                            )}
                                          </Button>
                                        ) : (
                                          column.name
                                        )}
                                      </TableHead>
                                    ))}
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {/* Dynamic WIP Items - with grouping support */}
                                  {wipGroupBy === "none" ? (
                                    // No grouping - show paginated items
                                    paginatedWipItems.map((item) => (
                                      <TableRow key={item.id}>
                                        <TableCell>
                                          <input
                                            type="checkbox"
                                            className="rounded"
                                            checked={selectedWipItems.includes(item.id)}
                                            onChange={() => handleSelectWipItem(item.id)}
                                          />
                                        </TableCell>
                                        {visibleWipColumns.map((column) => {
                                          const align = column.align === "right" ? "text-right" : "";
                                          switch (column.id) {
                                            case "date":
                                              return <TableCell key={column.id}>{item.date}</TableCell>;
                                            case "resource":
                                              return <TableCell key={column.id}>{item.resource}</TableCell>;
                                            case "task":
                                              return <TableCell key={column.id}>{item.task}</TableCell>;
                                            case "description":
                                              return <TableCell key={column.id}>{item.description}</TableCell>;
                                            case "type":
                                              return (
                                                <TableCell key={column.id}>
                                                  <Badge variant={item.type === "Time" ? "outline" : "secondary"}>
                                                    {item.type}
                                                  </Badge>
                                                </TableCell>
                                              );
                                            case "quantity":
                                              return (
                                                <TableCell key={column.id} className={align}>
                                                  {item.quantity.toFixed(2)}
                                                </TableCell>
                                              );
                                            case "rate":
                                              return (
                                                <TableCell key={column.id} className={align}>
                                                  <SensitiveData>{formatCurrency(item.rate)}</SensitiveData>
                                                </TableCell>
                                              );
                                            case "amount":
                                              return (
                                                <TableCell key={column.id} className={align}>
                                                  <SensitiveData>{formatCurrency(item.amount)}</SensitiveData>
                                                </TableCell>
                                              );
                                            case "costCentre":
                                              return <TableCell key={column.id}>{item.costCentre}</TableCell>;
                                            default:
                                              return <TableCell key={column.id}>-</TableCell>;
                                          }
                                        })}
                                      </TableRow>
                                    ))
                                  ) : (
                                    // Grouping enabled - show grouped items
                                    groupedWipItems &&
                                    Object.entries(groupedWipItems).map(([groupKey, groupItems]) => {
                                      const groupTotal = groupItems.reduce((sum, item) => sum + item.amount, 0);
                                      const groupHours = groupItems
                                        .filter((item) => item.type === "Time")
                                        .reduce((sum, item) => sum + item.quantity, 0);

                                      return (
                                        <>
                                          {/* Group Header Row */}
                                          <TableRow key={`group-${groupKey}`} className="bg-muted/30">
                                            <TableCell colSpan={visibleWipColumns.findIndex((c) => c.id === "quantity") + 1} className="font-semibold">
                                              {groupKey} ({groupItems.length} items)
                                            </TableCell>
                                            {visibleWipColumns.find((c) => c.id === "quantity") && (
                                              <TableCell className="text-right font-semibold">
                                                {groupHours > 0 ? `${groupHours.toFixed(1)} hrs` : "-"}
                                              </TableCell>
                                            )}
                                            {visibleWipColumns.find((c) => c.id === "rate") && <TableCell></TableCell>}
                                            {visibleWipColumns.find((c) => c.id === "amount") && (
                                              <TableCell className="text-right font-semibold">
                                                <SensitiveData>{formatCurrency(groupTotal)}</SensitiveData>
                                              </TableCell>
                                            )}
                                            {visibleWipColumns.find((c) => c.id === "costCentre") && <TableCell></TableCell>}
                                          </TableRow>
                                          {/* Group Items */}
                                          {groupItems.map((item) => (
                                            <TableRow key={item.id}>
                                              <TableCell>
                                                <input
                                                  type="checkbox"
                                                  className="rounded"
                                                  checked={selectedWipItems.includes(item.id)}
                                                  onChange={() => handleSelectWipItem(item.id)}
                                                />
                                              </TableCell>
                                              {visibleWipColumns.map((column) => {
                                                const align = column.align === "right" ? "text-right" : "";
                                                switch (column.id) {
                                                  case "date":
                                                    return <TableCell key={column.id}>{item.date}</TableCell>;
                                                  case "resource":
                                                    return <TableCell key={column.id}>{item.resource}</TableCell>;
                                                  case "task":
                                                    return <TableCell key={column.id}>{item.task}</TableCell>;
                                                  case "description":
                                                    return <TableCell key={column.id}>{item.description}</TableCell>;
                                                  case "type":
                                                    return (
                                                      <TableCell key={column.id}>
                                                        <Badge variant={item.type === "Time" ? "outline" : "secondary"}>
                                                          {item.type}
                                                        </Badge>
                                                      </TableCell>
                                                    );
                                                  case "quantity":
                                                    return (
                                                      <TableCell key={column.id} className={align}>
                                                        {item.quantity.toFixed(2)}
                                                      </TableCell>
                                                    );
                                                  case "rate":
                                                    return (
                                                      <TableCell key={column.id} className={align}>
                                                        <SensitiveData>{formatCurrency(item.rate)}</SensitiveData>
                                                      </TableCell>
                                                    );
                                                  case "amount":
                                                    return (
                                                      <TableCell key={column.id} className={align}>
                                                        <SensitiveData>{formatCurrency(item.amount)}</SensitiveData>
                                                      </TableCell>
                                                    );
                                                  case "costCentre":
                                                    return <TableCell key={column.id}>{item.costCentre}</TableCell>;
                                                  default:
                                                    return <TableCell key={column.id}>-</TableCell>;
                                                }
                                              })}
                                            </TableRow>
                                          ))}
                                        </>
                                      );
                                    })
                                  )}
                                  {/* Totals Row */}
                                  <TableRow className="bg-muted/50 font-semibold border-t-2">
                                    <TableCell colSpan={visibleWipColumns.findIndex((c) => c.id === "quantity") + 1} className="text-right">
                                      Selected Total ({selectedWipTotals.count} items):
                                    </TableCell>
                                    {visibleWipColumns.find((c) => c.id === "quantity") && (
                                      <TableCell className="text-right">
                                        {selectedWipTotals.hours > 0 ? `${selectedWipTotals.hours.toFixed(1)} hrs` : "-"}
                                      </TableCell>
                                    )}
                                    {visibleWipColumns.find((c) => c.id === "rate") && <TableCell></TableCell>}
                                    {visibleWipColumns.find((c) => c.id === "amount") && (
                                      <TableCell className="text-right">
                                        <SensitiveData>{formatCurrency(selectedWipTotals.amount)}</SensitiveData>
                                      </TableCell>
                                    )}
                                    {visibleWipColumns.find((c) => c.id === "costCentre") && <TableCell></TableCell>}
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between mt-4">
                              <div className="text-sm text-muted-foreground">
                                Showing {wipStartItem}-{wipEndItem} of {wipTotalItems} items
                                {selectedWipItems.length > 0 && ` (${selectedWipItems.length} selected)`}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground mr-2">
                                  Page {wipCurrentPage} of {wipTotalPages || 1}
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={wipCurrentPage === 1}
                                  onClick={() => handleWipPageChange(wipCurrentPage - 1)}
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                  Previous
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={wipCurrentPage >= wipTotalPages}
                                  onClick={() => handleWipPageChange(wipCurrentPage + 1)}
                                >
                                  Next
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
              </TabsContent>

              {/* T&M Sales Budget Tab */}
              {isBudgetingEnabled && budgetPermissions.settings.permissions.salesBudgetEnabled && (
                <TabsContent value="sales-budget" className="space-y-4">
                  {budgetLoading ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading budget data...</p>
                      </div>
                    </div>
                  ) : budgetError ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center space-y-4">
                        <div className="text-destructive mb-2">Error loading budget data</div>
                        <p className="text-sm text-muted-foreground">{budgetError}</p>
                      </div>
                    </div>
                  ) : (
                    <SalesBudgetView
                      projectId={numericProjectId}
                      data={budgetData?.salesBudget}
                      costBudget={budgetData?.costBudget}
                      projectType={project.type}
                      billingTriggerType={project.billingTriggerType}
                      completionPercent={project.type === 'fixed-price' ? effectiveCompletionPercent : undefined}
                      completionPercentMode={project.type === 'fixed-price' ? (project.completionPercentMode ?? 'based-on-time') : undefined}
                      completionPercentOverriddenBy={project.type === 'fixed-price' ? project.completionPercentOverriddenBy : undefined}
                      invoicedToDate={project.type === 'fixed-price' ? fixedFeeInvoicedToDate : undefined}
                      milestoneInvoices={project.type === 'fixed-price' ? fixedFeeInvoices : undefined}
                      onCreateBudget={() => setSalesBudgetEditModalOpen(true)}
                      onEdit={() => {
                        // Use page view for T&M projects, modal for Fixed Price
                        if (project.type === "time-and-materials") {
                          setEditSalesBudgetPageOpen(true);
                        } else {
                          setSalesBudgetEditModalOpen(true);
                        }
                      }}
                      onExport={() => {
                        setActiveBudgetType("sales");
                        setBudgetExportModalOpen(true);
                      }}
                      onHistory={() => {
                        setActiveBudgetType("sales");
                        setBudgetHistoryModalOpen(true);
                      }}
                    />
                  )}
                </TabsContent>
              )}

              {/* Cost Budget Tab */}
              {isBudgetingEnabled && budgetPermissions.settings.permissions.costBudgetEnabled && (
                <TabsContent value="cost-budget" className="space-y-4">
                  {budgetLoading ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading budget data...</p>
                      </div>
                    </div>
                  ) : budgetError ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center space-y-4">
                        <div className="text-destructive mb-2">Error loading budget data</div>
                        <p className="text-sm text-muted-foreground">{budgetError}</p>
                      </div>
                    </div>
                  ) : budgetData ? (
                    <>
                      <CostBudgetView
                        projectId={numericProjectId}
                        resourceBudget={budgetData.resourceBudget}
                        data={budgetData.costBudget}
                        onEdit={() => setEditCostBudgetPageOpen(true)}
                        onExport={() => {
                          setActiveBudgetType("cost");
                          setBudgetExportModalOpen(true);
                        }}
                        onHistory={() => {
                          setActiveBudgetType("cost");
                          setBudgetHistoryModalOpen(true);
                        }}
                      />

                      {budgetData.alerts && budgetData.alerts.length > 0 && (
                        <BudgetAlertsSection alerts={budgetData.alerts} />
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center min-h-[500px]">
                      <Card className="w-full max-w-md border-dashed">
                        <CardContent className="flex flex-col items-center justify-center text-center p-12">
                          <div className="rounded-full bg-muted p-4 mb-4">
                            <Receipt className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <h3 className="text-lg font-semibold mb-2">No Cost Budget</h3>
                          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                            Track expenses, commitments, and cost categories by creating a cost budget for this project.
                          </p>
                          <Button onClick={() => setEditCostBudgetPageOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Cost Budget
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Resource Budget Tab */}
              {isBudgetingEnabled && isResourceBudgetEnabled && (
                <TabsContent value="resource-budget" className="space-y-4">
                  {budgetLoading ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading budget data...</p>
                      </div>
                    </div>
                  ) : budgetError ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center space-y-4">
                        <div className="text-destructive mb-2">Error loading budget data</div>
                        <p className="text-sm text-muted-foreground">{budgetError}</p>
                      </div>
                    </div>
                  ) : budgetData && enrichedBudgetData?.resourceBudget ? (
                    <>
                      <ResourceBudgetWrapper
                        projectId={numericProjectId}
                        data={enrichedBudgetData.resourceBudget}
                        onEdit={() => setEditResourceBudgetPageOpen(true)}
                        onExport={() => {
                          setActiveBudgetType("resource");
                          setBudgetExportModalOpen(true);
                        }}
                        onHistory={() => {
                          setActiveBudgetType("resource");
                          setBudgetHistoryModalOpen(true);
                        }}
                        availablePeople={employees}
                        existingAllocations={resourceAllocations}
                        projectStartDate={project?.startDate}
                        projectEndDate={project?.endDate}
                        onAssign={async (role, selections) => {
                          const dataMode = getDataMode();
                          const totalAssignedHours = selections.reduce((sum, s) => sum + s.allocatedHours, 0);

                          if (dataMode === "live") {
                            try {
                              const now = new Date();
                              const startDate = project?.startDate || now.toISOString().split("T")[0];
                              const endDate =
                                project?.endDate ||
                                new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

                              for (const selection of selections) {
                                const allocationData = {
                                  projectId: numericProjectId,
                                  employeeId: selection.personId,
                                  role: role,
                                  allocatedHours: selection.allocatedHours,
                                  startDate: startDate,
                                  endDate: endDate,
                                  status: "confirmed" as const,
                                };
                                await createResourceAllocation(allocationData);
                              }

                              let resourceBudgetId = enrichedBudgetData?.resourceBudget?.id;
                              if (!resourceBudgetId) {
                                console.warn(
                                  "[onAssign/ResourceBudget] resourceBudgetId not found in local state, fetching from API...",
                                );
                                resourceBudgetId =
                                  (await fetchResourceBudgetIdForProject(numericProjectId)) ?? undefined;
                              }
                              if (resourceBudgetId) {
                                const matchedRole = enrichedBudgetData?.resourceBudget?.byRole?.find(
                                  (r) => r.role === role,
                                );
                                for (const selection of selections) {
                                  const employee = employees.find((e) => e.id === selection.personId);
                                  try {
                                    await createPersonBudget({
                                      resourceBudgetId,
                                      personId: selection.personId,
                                      personName: employee?.name || selection.personName,
                                      roleId: matchedRole?.roleId,
                                      budgetHours: selection.allocatedHours,
                                      costRate: 0,
                                    });
                                  } catch (pbError) {
                                    console.warn("Failed to create PersonBudget record:", pbError);
                                  }
                                }
                              } else {
                                console.warn(
                                  "[onAssign/ResourceBudget] No resourceBudgetId available — skipping PersonBudget creation",
                                );
                              }
                            } catch (error) {
                              console.error("Failed to create resource allocations:", error);
                              toast.error("Failed to assign team members to project");
                              return;
                            }
                          }

                          if (enrichedBudgetData?.resourceBudget?.byRole) {
                            const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map((r) => {
                              if (r.role === role) {
                                return { ...r, allocatedHours: r.allocatedHours + totalAssignedHours };
                              }
                              return r;
                            });

                            const newPersonBudgets = selections.map((selection) => {
                              const employee = employees.find((e) => e.id === selection.personId);
                              return {
                                id: -Date.now() - selection.personId,
                                personId: selection.personId,
                                personName: employee?.name || selection.personName,
                                role: role,
                                budgetHours: selection.allocatedHours,
                                actualHours: 0,
                                utilisation: 0,
                                costRate: 0,
                                budgetCost: 0,
                                actualCost: 0,
                              };
                            });

                            const existingByPerson = enrichedBudgetData.resourceBudget.byPerson || [];

                            setLocalBudgetData({
                              ...enrichedBudgetData,
                              resourceBudget: {
                                ...enrichedBudgetData.resourceBudget,
                                byRole: updatedRoles,
                                byPerson: [...existingByPerson, ...newPersonBudgets],
                              },
                            });
                          }

                          toast.success(
                            `Assigned ${selections.length} team member${selections.length !== 1 ? "s" : ""} to ${role}`,
                          );
                        }}
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-center min-h-[500px]">
                      <Card className="w-full max-w-md border-dashed">
                        <CardContent className="flex flex-col items-center justify-center text-center p-12">
                          <div className="rounded-full bg-muted p-4 mb-4">
                            <Clock className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <h3 className="text-lg font-semibold mb-2">No Resource Budget</h3>
                          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                            Plan team capacity and track hours by creating a resource budget for this project.
                          </p>
                          <Button onClick={() => setEditResourceBudgetPageOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Resource Budget
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Cost Centres Tab */}
              <TabsContent value="cost-centres" className="space-y-4">
                {/* Header Section */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3 ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCostCentreColumnConfigOpen(true)}
                      className="gap-2"
                    >
                      <Settings2 className="h-4 w-4" />
                      Configure Columns
                    </Button>
                    {!isStructureView && (
                      <Button size="sm" onClick={() => setCreateCostCentrePanelOpen(true)} className="gap-2">
                        <Plus className="h-4 w-4" />
                        New
                      </Button>
                    )}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="relative px-3" aria-label="Recycle Bin">
                            <Trash2 className="h-4 w-4" />
                            <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] font-medium bg-muted text-muted-foreground rounded-full border">
                              0
                            </span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Recycle Bin</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {isStructureView && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfigureStructureOpen(true)}
                        className="gap-2"
                      >
                        <Settings2 className="h-4 w-4" />
                        Configure Structure
                      </Button>
                    )}
                    <div className="flex items-center gap-2">
                      <label htmlFor="structure-view-toggle" className="text-sm font-medium cursor-pointer">
                        Structure View
                      </label>
                      <Switch
                        id="structure-view-toggle"
                        checked={isStructureView}
                        onCheckedChange={setIsStructureView}
                      />
                    </div>
                  </div>
                </div>

                {/* Content Display Area */}
                {isStructureView ? (
                  // Structure View (Tree/Hierarchy)
                  <CostCentresHierarchicalTable
                    data={hierarchicalCostCentres}
                    columns={costCentreColumns}
                    useCardWrapper={false}
                    onRowClick={(costCentre) => {
                      setSelectedCostCentre(costCentre);
                      setCostCentreDetailsPanelOpen(true);
                    }}
                  />
                ) : (
                  // List/Table View
                  <CostCentresTable
                    data={costCentres}
                    columns={costCentreColumns}
                    useCardWrapper={false}
                    onRowClick={(costCentre) => {
                      setSelectedCostCentre(costCentre);
                      setCostCentreDetailsPanelOpen(true);
                    }}
                  />
                )}
              </TabsContent>

              {/* Column Configurator Modal */}
              <CostCentresColumnConfigurator
                open={costCentreColumnConfigOpen}
                onOpenChange={setCostCentreColumnConfigOpen}
                columns={costCentreColumns}
                onSave={(newColumns) => {
                  setCostCentreColumns(newColumns);
                  // Persist to localStorage as fallback
                  localStorage.setItem('cost-centre-column-preferences', JSON.stringify(newColumns));
                  // Persist to API in live mode
                  const mode = getDataMode();
                  if (mode === 'live') {
                    saveColumnPreferences(newColumns as unknown as Parameters<typeof saveColumnPreferences>[0], 'cost-centre-column-preferences').catch((error) => {
                      console.error('Failed to save cost centre column preferences to API:', error);
                    });
                  }
                }}
              />

              {/* Create Cost Centre Panel */}
              <CreateCostCentrePanel
                open={createCostCentrePanelOpen}
                onOpenChange={setCreateCostCentrePanelOpen}
                existingCodes={[...costCentres.map((cc) => cc.code), ...hierarchicalCostCentres.map((cc) => cc.code)]}
                onSave={async (newCostCentre) => {
                  const mode = getDataMode();

                  if (mode === "live" && projectId) {
                    // Live mode - call the API to persist the cost centre
                    try {
                      const createdCostCentre = await createCostCentre({
                        projectId: numericProjectId,
                        code: newCostCentre.code,
                        name: newCostCentre.name,
                        type: newCostCentre.type,
                        status: newCostCentre.status,
                        billable: newCostCentre.billable,
                        owner: newCostCentre.owner,
                        description: newCostCentre.description,
                        costsBudget: newCostCentre.costsBudget,
                        revenueBudget: newCostCentre.revenueBudget,
                        hoursBudget: newCostCentre.hoursBudget,
                      });

                      // Use the ID from the API response
                      const costCentreWithId: CostCentre = {
                        ...newCostCentre,
                        id: createdCostCentre.id || -Date.now(),
                      };

                      // Add to both flat and hierarchical lists
                      setCostCentres((prev) => [...prev, costCentreWithId]);
                      setHierarchicalCostCentres((prev) => [...prev, { ...costCentreWithId, children: [] }]);

                      console.log("Created new cost centre via API:", costCentreWithId);
                    } catch (error) {
                      console.error("Failed to create cost centre:", error);
                      // Still add to local state for demo purposes, but log the error
                      const id = -Date.now();
                      const costCentreWithId: CostCentre = {
                        ...newCostCentre,
                        id,
                      };
                      setCostCentres((prev) => [...prev, costCentreWithId]);
                      setHierarchicalCostCentres((prev) => [...prev, { ...costCentreWithId, children: [] }]);
                    }
                  } else {
                    // Demo mode - just update local state
                    const id = -Date.now();
                    const costCentreWithId: CostCentre = {
                      ...newCostCentre,
                      id,
                    };

                    // Add to both flat and hierarchical lists
                    setCostCentres((prev) => [...prev, costCentreWithId]);
                    setHierarchicalCostCentres((prev) => [...prev, { ...costCentreWithId, children: [] }]);

                    console.log("Created new cost centre (demo mode):", costCentreWithId);
                  }
                }}
              />

              {/* Purchases Tab */}
              <TabsContent value="purchases" className="space-y-4">
                <PurchasesCommitmentsTab projectId={numericProjectId} projectCode={project?.code} employees={employees} />
              </TabsContent>

              {/* Team Tab */}
              <TabsContent value="team" className="space-y-4">
                {/* Unfulfilled Roles Table */}
                {enrichedBudgetData?.resourceBudget?.byRole && project && (
                  <UnfulfilledRolesTable
                    roles={enrichedBudgetData.resourceBudget.byRole}
                    projectId={numericProjectId}
                    projectStartDate={project.startDate}
                    projectEndDate={project.endDate}
                    availablePeople={employees}
                    existingAllocations={resourceAllocations}
                    onAssign={async (role, selections) => {
                      // Handle assignment - update resource allocations and budget data
                      const dataMode = getDataMode();

                      // Calculate total hours being assigned
                      const totalAssignedHours = selections.reduce((sum, s) => sum + s.allocatedHours, 0);

                      // In live mode, create resource allocations via API
                      const createdAllocationIds: Map<number, string> = new Map();
                      if (dataMode === "live") {
                        try {
                          const now = new Date();
                          const startDate = project?.startDate || now.toISOString().split("T")[0];
                          const endDate =
                            project?.endDate ||
                            new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

                          // Create resource allocations for each selection
                          for (const selection of selections) {
                            const allocationData = {
                              projectId: numericProjectId,
                              employeeId: selection.personId,
                              role: role,
                              allocatedHours: selection.allocatedHours,
                              startDate: startDate,
                              endDate: endDate,
                              status: "confirmed" as const,
                            };

                            const createdAllocation = await createResourceAllocation(allocationData);
                            createdAllocationIds.set(selection.personId, createdAllocation.id);
                          }
                          // Also create PersonBudget records so Rate Card can show assigned people
                          let resourceBudgetId = enrichedBudgetData?.resourceBudget?.id;
                          if (!resourceBudgetId) {
                            console.warn(
                              "[onAssign/Team] resourceBudgetId not found in local state, fetching from API...",
                            );
                            resourceBudgetId = (await fetchResourceBudgetIdForProject(numericProjectId)) ?? undefined;
                          }
                          if (resourceBudgetId) {
                            const matchedRole = enrichedBudgetData?.resourceBudget?.byRole?.find(
                              (r) => r.role === role,
                            );
                            for (const selection of selections) {
                              const employee = employees.find((e) => e.id === selection.personId);
                              try {
                                await createPersonBudget({
                                  resourceBudgetId,
                                  personId: selection.personId,
                                  personName: employee?.name || selection.personName,
                                  roleId: matchedRole?.roleId,
                                  budgetHours: selection.allocatedHours,
                                  costRate: 0,
                                });
                              } catch (pbError) {
                                console.warn("Failed to create PersonBudget record:", pbError);
                              }
                            }
                          } else {
                            console.warn(
                              "[onAssign/Team] No resourceBudgetId available — skipping PersonBudget creation",
                            );
                          }
                        } catch (error) {
                          console.error("Failed to create resource allocations:", error);
                          toast.error("Failed to assign team members to project");
                          return;
                        }
                      }

                      // Update budget data to reflect the new assignments
                      if (enrichedBudgetData?.resourceBudget?.byRole) {
                        const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map((r) => {
                          if (r.role === role) {
                            return {
                              ...r,
                              allocatedHours: r.allocatedHours + totalAssignedHours,
                            };
                          }
                          return r;
                        });

                        const newPersonBudgets = selections.map((selection) => {
                          const employee = employees.find((e) => e.id === selection.personId);
                          return {
                            id: -Date.now() - selection.personId,
                            personId: selection.personId,
                            personName: employee?.name || selection.personName,
                            role: role,
                            budgetHours: selection.allocatedHours,
                            actualHours: 0,
                            utilisation: 0,
                            costRate: 0,
                            budgetCost: 0,
                            actualCost: 0,
                          };
                        });

                        const existingByPerson = enrichedBudgetData.resourceBudget.byPerson || [];

                        setLocalBudgetData({
                          ...enrichedBudgetData,
                          resourceBudget: {
                            ...enrichedBudgetData.resourceBudget,
                            byRole: updatedRoles,
                            byPerson: [...existingByPerson, ...newPersonBudgets],
                          },
                        });
                      }

                      // Update team members - either add new or update existing
                      setTeamMembers((prev) => {
                        const updatedMembers = [...prev];
                        const newMembers: TeamMember[] = [];

                        selections.forEach((selection) => {
                          // Check if team member already exists (by employeeId)
                          const existingIndex = updatedMembers.findIndex((tm) => tm.employeeId === selection.personId);

                          const employee = employees.find((e) => e.id === selection.personId);
                          const now = new Date();
                          const startDate = project?.startDate || now.toISOString();
                          const endDate =
                            project?.endDate || new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();

                          if (existingIndex !== -1) {
                            // Team member already exists - update their allocated hours
                            const existing = updatedMembers[existingIndex];
                            const newAllocatedHours = existing.allocatedHours + selection.allocatedHours;
                            const remaining = newAllocatedHours - existing.actualHours;
                            const utilisationPercent = (existing.actualHours / newAllocatedHours) * 100;

                            // Determine updated status
                            let status: TeamMember["status"];
                            if (utilisationPercent > 100) {
                              status = "Over Allocated";
                            } else if (utilisationPercent > 85) {
                              status = "At Risk";
                            } else {
                              status = "On Track";
                            }

                            // Update existing team member
                            updatedMembers[existingIndex] = {
                              ...existing,
                              allocatedHours: newAllocatedHours,
                              remaining,
                              utilisationPercent,
                              status,
                            };
                          } else {
                            // Team member doesn't exist - create new entry
                            // In live mode, actual hours start at 0; in demo mode, generate mock data
                            const actualHours =
                              dataMode === "live"
                                ? 0
                                : Math.floor(selection.allocatedHours * (0.3 + Math.random() * 0.4));
                            const remaining = selection.allocatedHours - actualHours;
                            const utilisationPercent =
                              selection.allocatedHours > 0 ? (actualHours / selection.allocatedHours) * 100 : 0;

                            // Determine status
                            let status: TeamMember["status"];
                            if (utilisationPercent > 100) {
                              status = "Over Allocated";
                            } else if (utilisationPercent > 85) {
                              status = "At Risk";
                            } else {
                              status = "On Track";
                            }

                            // Generate avatar URL
                            const name = employee?.name || selection.personName;
                            const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

                            // Use the created allocation ID in live mode, or generate a temp ID in demo mode
                            const allocationId =
                              createdAllocationIds.get(selection.personId) ||
                              `tm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                            newMembers.push({
                              id: allocationId,
                              employeeId: selection.personId,
                              name: name,
                              email: employee?.email || "",
                              role: employee?.role || role, // Use employee's actual role, fallback to unfulfilled role
                              avatar: avatarUrl,
                              allocatedHours: selection.allocatedHours,
                              actualHours,
                              remaining,
                              startDate,
                              endDate,
                              status,
                              utilisationPercent,
                            });
                          }
                        });

                        // Return updated members plus any new members
                        return [...updatedMembers, ...newMembers];
                      });

                      toast.success(
                        `Assigned ${selections.length} team member${selections.length !== 1 ? "s" : ""} to ${role} role`,
                        {
                          description: selections.map((s) => `${s.personName}: ${s.allocatedHours}h`).join(", "),
                        },
                      );
                    }}
                  />
                )}

                {/* Team Management Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Team Management</CardTitle>
                    <CardDescription>Comprehensive team member tracking and allocation</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]"></TableHead>
                          <TableHead>
                            <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort("name")}>
                              Name
                              <ArrowUpDown className="ml-2 h-4 w-4" />
                            </Button>
                          </TableHead>
                          <TableHead>
                            <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort("role")}>
                              Role
                              <ArrowUpDown className="ml-2 h-4 w-4" />
                            </Button>
                          </TableHead>
                          <TableHead className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="-mr-3 h-8"
                              onClick={() => handleSort("allocatedHours")}
                            >
                              Allocated Hours
                              <ArrowUpDown className="ml-2 h-4 w-4" />
                            </Button>
                          </TableHead>
                          <TableHead className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="-mr-3 h-8"
                              onClick={() => handleSort("actualHours")}
                            >
                              Actual Hours
                              <ArrowUpDown className="ml-2 h-4 w-4" />
                            </Button>
                          </TableHead>
                          <TableHead className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="-mr-3 h-8"
                              onClick={() => handleSort("remaining")}
                            >
                              Remaining
                              <ArrowUpDown className="ml-2 h-4 w-4" />
                            </Button>
                          </TableHead>
                          <TableHead>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="-ml-3 h-8"
                              onClick={() => handleSort("status")}
                            >
                              Status
                              <ArrowUpDown className="ml-2 h-4 w-4" />
                            </Button>
                          </TableHead>
                          <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedTeamMembers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center text-muted-foreground h-32">
                              No team members found
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedTeamMembers.map((member) => {
                            // TODO: Access Evo Integration - When integrating with Access Evo platform,
                            // attempt to fetch the user's Evo avatar from the platform API. If no avatar
                            // exists for the user, fall back to the EDS color scheme below with initials.
                            // Example: const evoAvatarUrl = await fetchEvoUserAvatar(member.platformUserId);

                            // Generate deterministic color based on name for EDS styling (fallback when no Evo avatar)
                            const getAvatarColorClass = (name: string) => {
                              const colors = [
                                "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
                                "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300",
                                "bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-300",
                                "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300",
                                "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
                                "bg-info-100 text-info-700 dark:bg-info-900/30 dark:text-info-300",
                                "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
                                "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
                              ];
                              const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
                              return colors[hash % colors.length];
                            };

                            return (
                              <TableRow key={member.id}>
                                <TableCell>
                                  <Avatar className="h-8 w-12 rounded-md">
                                    <AvatarFallback
                                      className={`text-xs rounded-md ${getAvatarColorClass(member.name)}`}
                                    >
                                      {getInitials(member.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                </TableCell>
                                <TableCell className="font-medium">{member.name}</TableCell>
                                <TableCell>{member.role}</TableCell>
                                <TableCell className="text-right font-sans">
                                  {member.allocatedHours === 0 ? (
                                    <span className="text-muted-foreground italic">Not allocated</span>
                                  ) : (
                                    `${member.allocatedHours.toFixed(0)}h`
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-sans">{member.actualHours.toFixed(0)}h</TableCell>
                                <TableCell className="text-right font-sans">
                                  <span className={member.remaining < 0 ? "text-error-600" : ""}>
                                    {member.remaining.toFixed(0)}h
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={getStatusBadgeVariant(member.status, member.utilisationPercent)}>
                                    {member.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {member.actualHours === 0 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                            onClick={() => handleRemoveTeamMember(member)}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>Remove from project</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Milestones Tab */}
              <TabsContent value="milestones" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Project Milestones</CardTitle>
                    <CardDescription>Key deliverables and payment milestones</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Milestone</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Completed</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Payment</TableHead>
                          <TableHead>Paid</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {milestones.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground">
                              No milestones defined
                            </TableCell>
                          </TableRow>
                        ) : (
                          milestones.map((milestone) => (
                            <TableRow key={milestone.id}>
                              <TableCell className="font-medium">{milestone.name}</TableCell>
                              <TableCell>{formatDate(milestone.dueDate)}</TableCell>
                              <TableCell>
                                {milestone.completedDate ? formatDate(milestone.completedDate) : "-"}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    milestone.status === "completed"
                                      ? "default"
                                      : milestone.status === "overdue"
                                        ? "destructive"
                                        : "outline"
                                  }
                                >
                                  {milestone.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {milestone.paymentAmount ? (
                                  <SensitiveData>{formatCurrency(milestone.paymentAmount)}</SensitiveData>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>
                                {milestone.paymentReceived ? (
                                  <CheckCircle className="h-4 w-4 text-success-500" />
                                ) : (
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Time Tab */}
              <TabsContent value="time" className="space-y-4">
                <TimeEntriesTab
                  projectId={numericProjectId}
                  projectCode={project?.code}
                  timeEntries={timeEntries}
                  employees={employees}
                />
              </TabsContent>

              {/* Expenses Tab */}
              <TabsContent value="expenses" className="space-y-4">
                <div className="flex items-center gap-6 mb-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="include-approved-expenses"
                      checked={includeApprovedExpenses}
                      onCheckedChange={(checked) => setIncludeApprovedExpenses(checked === true)}
                    />
                    <label
                      htmlFor="include-approved-expenses"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Approved Expenses
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="include-credit-card-expenses"
                      checked={includeCreditCardExpenses}
                      onCheckedChange={(checked) => setIncludeCreditCardExpenses(checked === true)}
                    />
                    <label
                      htmlFor="include-credit-card-expenses"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Credit Card Expenses
                    </label>
                  </div>
                  {expensesLoading && <span className="text-sm text-muted-foreground">Loading...</span>}
                </div>
                <ExpenseEntriesTab projectId={numericProjectId} expenses={expenses} employees={employees} />
              </TabsContent>

              {/* Change Requests Tab */}
              <TabsContent value="changes" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Change Requests</CardTitle>
                    <CardDescription>Scope changes and their impact on budget and schedule</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Requested By</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Budget Impact</TableHead>
                          <TableHead className="text-right">Schedule Impact</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {changeRequests.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground">
                              No change requests
                            </TableCell>
                          </TableRow>
                        ) : (
                          changeRequests.map((cr) => (
                            <TableRow key={cr.id}>
                              <TableCell className="font-medium">{cr.title}</TableCell>
                              <TableCell>{cr.requestedBy}</TableCell>
                              <TableCell>{formatDate(cr.requestedDate)}</TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    cr.status === "approved"
                                      ? "default"
                                      : cr.status === "rejected"
                                        ? "destructive"
                                        : "outline"
                                  }
                                >
                                  {cr.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <SensitiveData>{formatCurrency(cr.impactBudget)}</SensitiveData>
                              </TableCell>
                              <TableCell className="text-right">
                                {cr.impactSchedule > 0 ? `+${cr.impactSchedule}` : cr.impactSchedule} days
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Settings Tab - Removed, now accessible via Configure button in Billing tab */}

              {/* Audit Tab — Phase 17 */}
              <TabsContent value="audit" className="space-y-4">
                <ProjectAuditTab
                  projectCode={project?.code ?? String(project?.id ?? '')}
                  projectType={project?.type ?? ''}
                  billingTriggerType={project?.billingTriggerType}
                  completionAuditEntries={completionAuditEntries}
                  fixedFeeInvoices={project?.type === 'fixed-price' ? auditInvoices : []}
                  initialCategoryFilter={auditCategoryFilter as "all" | "completion" | "status" | "financial" | "billing" | "invoice" | "budget"}
                />
              </TabsContent>
            </ScrollableTabsContainer>
          </div>
        </Tabs>
      </TooltipProvider>

      {/* Configure Structure Modal */}
      <ConfigureStructureModal
        open={configureStructureOpen}
        onOpenChange={setConfigureStructureOpen}
        projectName={project?.name || ""}
        costCentres={hierarchicalCostCentres}
        onSave={async (updatedStructure) => {
          // TODO: Implement API call to save structure
          console.log("Saving structure:", updatedStructure);
          setHierarchicalCostCentres(updatedStructure);
          // Show success toast
        }}
      />

      {/* Cost Centre Details Panel */}
      <CostCentreDetailsPanel
        costCentre={selectedCostCentre}
        projectId={numericProjectId}
        open={costCentreDetailsPanelOpen}
        onOpenChange={setCostCentreDetailsPanelOpen}
        onEdit={(updatedCostCentre) => {
          // Update the cost centre in the state to refresh the table
          setCostCentres((prev) => prev.map((cc) => (cc.id === updatedCostCentre.id ? updatedCostCentre : cc)));
          setHierarchicalCostCentres((prev) =>
            prev.map((cc) => (cc.id === updatedCostCentre.id ? { ...cc, ...updatedCostCentre } : cc)),
          );
          // Also update the selected cost centre if it's the same one
          if (selectedCostCentre?.id === updatedCostCentre.id) {
            setSelectedCostCentre(updatedCostCentre);
          }
        }}
        onDelete={(costCentreId) => {
          // Delete the cost centre
          setCostCentres((prev) => prev.filter((cc) => cc.id !== costCentreId));
          setHierarchicalCostCentres((prev) => prev.filter((cc) => cc.id !== costCentreId));
          console.log("Deleted cost centre:", costCentreId);
          // In a real app, this would call an API to delete the cost centre
        }}
        onMarkInactive={(costCentreId) => {
          // Mark the cost centre as inactive
          console.log("Mark inactive cost centre:", costCentreId);
          // In a real app, this would call an API to update the cost centre status
        }}
        onExpandToFullView={() => {
          // Close the side drawer and open the full modal
          setCostCentreDetailsPanelOpen(false);
          setCostCentreFullModalOpen(true);
        }}
      />

      {/* Cost Centre Full Modal */}
      <CostCentreFullModal
        costCentre={selectedCostCentre}
        projectId={numericProjectId}
        allCostCentres={costCentres}
        open={costCentreFullModalOpen}
        onOpenChange={setCostCentreFullModalOpen}
        onCostCentreChange={(costCentre) => {
          setSelectedCostCentre(costCentre);
        }}
        onMinimize={() => {
          setCostCentreFullModalOpen(false);
          setCostCentreDetailsPanelOpen(true);
        }}
      />

      {/* Sticky Footer for Edit Mode */}
      {isEditMode && (
        <StickyFooter>
          <Button variant="outline" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button onClick={handleSaveChanges} disabled={!hasUnsavedChanges}>
            Save Changes
          </Button>
        </StickyFooter>
      )}

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>Discard Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Budget Edit Modals */}
      {budgetData && (
        <>
          <EditBudgetModal
            open={salesBudgetEditModalOpen}
            onOpenChange={setSalesBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type || "fixed-price"}
            billingTriggerType={project?.billingTriggerType}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="sales"
          />

          <EditBudgetModal
            open={costBudgetEditModalOpen}
            onOpenChange={setCostBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type || "fixed-price"}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="cost"
          />

          <EditBudgetModal
            open={resourceBudgetEditModalOpen}
            onOpenChange={setResourceBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type || "fixed-price"}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="resource"
          />

          <BudgetHistoryModal
            open={budgetHistoryModalOpen}
            onOpenChange={setBudgetHistoryModalOpen}
            revisions={budgetData.revisions || []}
            projectName={
              project?.code
                ? `${project.code} - ${project.name}`
                : project?.name || budgetData.projectName || `Project ${projectId}`
            }
            onRestore={handleRestoreRevision}
          />

          <BudgetExportModal
            open={budgetExportModalOpen}
            onOpenChange={setBudgetExportModalOpen}
            budgetData={budgetData}
          />
        </>
      )}

      {/* Billing Settings Drawer */}
      <Sheet open={billingSettingsDrawerOpen} onOpenChange={handleDrawerOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-4">
          <SheetHeader className="mb-4">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle>Billing Configuration</SheetTitle>
                <SheetDescription>Configure billing settings for this project</SheetDescription>
              </div>
              <div className="flex items-center gap-2">
                {!drawerEditMode ? (
                  <Button onClick={handleDrawerEdit} size="sm" variant="outline">
                    <Settings2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button onClick={handleDrawerCancel} size="sm" variant="outline" disabled={drawerSaving}>
                      Cancel
                    </Button>
                    <Button onClick={handleDrawerSave} size="sm" disabled={drawerSaving}>
                      {drawerSaving ? "Saving..." : "Save"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-3">
            {/* Note: This drawer shows the same settings as the Settings tab, but in a convenient side panel */}
            {project?.type === "time-and-materials" ? (
              <>
                {/* Billing Configuration Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Billing Configuration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Billing Frequency */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-billing-frequency" className="text-base font-semibold">
                        Billing Frequency
                      </Label>
                      <Select
                        value={drawerSettings.billingFrequency}
                        onValueChange={(value) => handleDrawerSettingsChange("billingFrequency", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-billing-frequency" className="w-full">
                          <SelectValue placeholder="Select billing frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    {/* Invoice Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-invoice-template" className="text-base font-semibold">
                        Invoice Template
                      </Label>
                      <Select
                        value={drawerSettings.invoiceTemplateId ? drawerSettings.invoiceTemplateId.toString() : (invoiceTemplates.find(t => t.isDefault)?.id || invoiceTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("invoiceTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-invoice-template" className="w-full">
                          <SelectValue placeholder="Select invoice template" />
                        </SelectTrigger>
                        <SelectContent>
                          {invoiceTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Choose the template to use when generating invoices for this project
                      </p>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="h-auto p-0 text-xs"
                        onClick={() => navigate('/settings/templates')}
                      >
                        Manage Templates →
                      </Button>
                    </div>

                    <Separator />

                    {/* Auto Generate Draft Invoice */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="drawer-auto-generate-toggle" className="text-base font-semibold">
                            Auto Generate Draft Invoice
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Automatically create draft invoices based on a schedule
                          </p>
                        </div>
                        <Switch
                          id="drawer-auto-generate-toggle"
                          checked={drawerSettings.autoGenerateEnabled}
                          onCheckedChange={(checked) => handleDrawerSettingsChange("autoGenerateEnabled", checked)}
                          disabled={!drawerEditMode}
                        />
                      </div>

                      {drawerSettings.autoGenerateEnabled && (
                        <div className="ml-0 space-y-2 pt-2">
                          {/* Monthly Pattern Options */}
                          {drawerSettings.billingFrequency === "monthly" && (
                            <div className="space-y-1.5">
                              <Label className="text-sm font-medium">Generate invoice on:</Label>
                              <RadioGroup
                                value={drawerSettings.monthlyPattern}
                                onValueChange={(value) => handleDrawerSettingsChange("monthlyPattern", value)}
                                className="space-y-2"
                                disabled={!drawerEditMode}
                              >
                                <div className="space-y-2">
                                  <RadioGroupItem value="specific-day" id="drawer-specific-day">
                                    Specific day of month
                                  </RadioGroupItem>
                                  {drawerSettings.monthlyPattern === "specific-day" && (
                                    <div className="space-y-1 ml-7">
                                      <Input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={drawerSettings.monthlyDay}
                                        disabled={!drawerEditMode}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value === "") {
                                            handleDrawerSettingsChange("monthlyDay", "");
                                            return;
                                          }
                                          const numValue = parseInt(value, 10);
                                          if (!isNaN(numValue) && numValue >= 1 && numValue <= 31) {
                                            handleDrawerSettingsChange("monthlyDay", value);
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const value = e.target.value;
                                          if (value === "" || parseInt(value, 10) < 1 || parseInt(value, 10) > 31) {
                                            handleDrawerSettingsChange("monthlyDay", "1");
                                          }
                                        }}
                                        className="w-32"
                                        placeholder="1-31"
                                        aria-invalid={
                                          drawerSettings.monthlyDay === "" ||
                                          parseInt(drawerSettings.monthlyDay, 10) < 1 ||
                                          parseInt(drawerSettings.monthlyDay, 10) > 31
                                        }
                                      />
                                      <p className="text-xs text-muted-foreground">Enter a day between 1 and 31</p>
                                    </div>
                                  )}
                                </div>

                                <RadioGroupItem value="last-day" id="drawer-last-day">
                                  Last day of the month
                                </RadioGroupItem>

                                <RadioGroupItem value="first-working-day" id="drawer-first-working-day">
                                  First working day of the month (Mon-Fri)
                                </RadioGroupItem>

                                <RadioGroupItem value="last-working-day" id="drawer-last-working-day">
                                  Last working day of the month (Mon-Fri)
                                </RadioGroupItem>
                              </RadioGroup>
                            </div>
                          )}

                          {/* Weekly Pattern Options */}
                          {drawerSettings.billingFrequency === "weekly" && (
                            <div className="space-y-1.5">
                              <Label htmlFor="drawer-weekly-day" className="text-sm font-medium">
                                Generate invoice on:
                              </Label>
                              <Select
                                value={drawerSettings.weeklyDay}
                                onValueChange={(value) => handleDrawerSettingsChange("weeklyDay", value)}
                                disabled={!drawerEditMode}
                              >
                                <SelectTrigger id="drawer-weekly-day" className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="monday">Monday</SelectItem>
                                  <SelectItem value="tuesday">Tuesday</SelectItem>
                                  <SelectItem value="wednesday">Wednesday</SelectItem>
                                  <SelectItem value="thursday">Thursday</SelectItem>
                                  <SelectItem value="friday">Friday</SelectItem>
                                  <SelectItem value="saturday">Saturday</SelectItem>
                                  <SelectItem value="sunday">Sunday</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                Draft invoices will be generated every week on this day
                              </p>
                            </div>
                          )}

                          {/* Preview of next invoice date */}
                          {(() => {
                            const nextDate = getNextInvoiceDateForDrawer();
                            if (nextDate) {
                              return (
                                <div className="rounded-lg bg-muted/50 border border-muted p-4 text-sm">
                                  <div className="flex items-start gap-3">
                                    <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                                    <div>
                                      <span className="font-semibold text-foreground">Preview</span>
                                      <p className="text-muted-foreground mt-1">
                                        Next invoice will generate on{" "}
                                        <span className="font-medium text-foreground">
                                          {nextDate.toLocaleDateString("en-US", {
                                            weekday: "long",
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                          })}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Recharge Configuration Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Recharge Configuration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Expense Recharge */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-expense-recharge" className="text-base font-semibold">Expense Recharge</Label>
                      <Select
                        value={drawerSettings.expenseRecharge}
                        onValueChange={(value) => {
                          handleDrawerSettingsChange("expenseRecharge", value);
                          if (value !== "markup") {
                            handleDrawerSettingsChange("expenseMarkupPercent", "");
                          }
                        }}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-expense-recharge">
                          <SelectValue placeholder="Select expense recharge method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="at-cost">At Cost</SelectItem>
                          <SelectItem value="markup">Mark up %</SelectItem>
                          <SelectItem value="not-rechargeable">Not Rechargeable</SelectItem>
                        </SelectContent>
                      </Select>

                      {drawerSettings.expenseRecharge === "markup" && (
                        <div className="ml-7 mt-1.5 space-y-1.5">
                          <Label htmlFor="drawer-expense-markup-percent" className="text-sm">
                            Markup Percentage
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="drawer-expense-markup-percent"
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="0.00"
                              value={drawerSettings.expenseMarkupPercent}
                              onChange={(e) => handleDrawerSettingsChange("expenseMarkupPercent", e.target.value)}
                              className="w-32"
                              disabled={!drawerEditMode}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Expenses will be billed at cost plus this percentage
                          </p>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Purchase Recharge */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-purchase-recharge" className="text-base font-semibold">Purchase Recharge</Label>
                      <Select
                        value={drawerSettings.purchaseRecharge}
                        onValueChange={(value) => {
                          handleDrawerSettingsChange("purchaseRecharge", value);
                          if (value !== "markup") {
                            handleDrawerSettingsChange("purchaseMarkupPercent", "");
                          }
                        }}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-purchase-recharge">
                          <SelectValue placeholder="Select purchase recharge method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="at-cost">At Cost</SelectItem>
                          <SelectItem value="markup">Mark up %</SelectItem>
                          <SelectItem value="not-rechargeable">Not Rechargeable</SelectItem>
                        </SelectContent>
                      </Select>

                      {drawerSettings.purchaseRecharge === "markup" && (
                        <div className="ml-7 mt-1.5 space-y-1.5">
                          <Label htmlFor="drawer-purchase-markup-percent" className="text-sm">
                            Markup Percentage
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="drawer-purchase-markup-percent"
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="0.00"
                              value={drawerSettings.purchaseMarkupPercent}
                              onChange={(e) => handleDrawerSettingsChange("purchaseMarkupPercent", e.target.value)}
                              className="w-32"
                              disabled={!drawerEditMode}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Purchases will be billed at cost plus this percentage
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Invoice Settings Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Banknote className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Invoice Settings</CardTitle>
                    </div>
                    <CardDescription>Configure invoice caps and billing limits for this project</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Not to Exceed Cap */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="drawer-not-to-exceed-cap" className="text-base font-semibold">
                            Invoice Cap
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Set a maximum amount that can be billed on each invoice
                          </p>
                        </div>
                        <Switch
                          id="drawer-not-to-exceed-cap"
                          checked={drawerSettings.notToExceedCapEnabled}
                          onCheckedChange={(checked) => {
                            handleDrawerSettingsChange("notToExceedCapEnabled", checked);
                            if (!checked) {
                              handleDrawerSettingsChange("notToExceedCapAmount", "");
                            }
                          }}
                          disabled={!drawerEditMode}
                        />
                      </div>

                      {drawerSettings.notToExceedCapEnabled && (
                        <div className="ml-0 mt-1.5 space-y-1.5">
                          <Label htmlFor="drawer-cap-amount" className="text-sm">
                            Maximum Amount Per Invoice
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="drawer-cap-amount"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={drawerSettings.notToExceedCapAmount}
                              onChange={(e) => handleDrawerSettingsChange("notToExceedCapAmount", e.target.value)}
                              className="w-40"
                              disabled={!drawerEditMode}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Any invoice exceeding this amount will require manual review
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : project?.type === "fixed-price" ? (
              <>
                {/* Fixed Fee — Billing Configuration Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Billing Configuration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Billing Trigger Type (read-only display) */}
                    <div className="space-y-1.5">
                      <Label className="text-base font-semibold">Billing Trigger Type</Label>
                      <p className="text-sm text-foreground">
                        {project.billingTriggerType === 'percent-complete' ? '% Complete' : project.billingTriggerType === 'date' ? 'Date-based' : '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Configured in the Sales Budget. Determines when billing milestones are evaluated.
                      </p>
                    </div>

                    {/* Task 13.1 — Completion % Calculation — only for percent-complete projects */}
                    {project.billingTriggerType === 'percent-complete' && (
                      <>
                        <Separator />
                        <div className="space-y-2">
                          <Label className="text-base font-semibold">Completion % Calculation</Label>
                          <p className="text-sm text-muted-foreground">
                            Controls how the project's completion percentage is determined for milestone evaluation.
                          </p>
                          <Select
                            value={project.completionPercentMode ?? 'based-on-time'}
                            onValueChange={(value) => {
                              if (drawerEditMode) {
                                handleCompletionModeChange(value as 'manual' | 'based-on-time');
                              }
                            }}
                            disabled={!drawerEditMode}
                          >
                            <SelectTrigger id="drawer-completion-mode" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="based-on-time">Based on Approved Time</SelectItem>
                              <SelectItem value="manual">Manual</SelectItem>
                            </SelectContent>
                          </Select>
                          {project.completionPercentMode === 'based-on-time' ? (
                            <p className="text-xs text-muted-foreground">
                              Completion % is automatically calculated from approved hours ÷ budget hours. Any existing override will be cleared when switching to this mode.
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Completion % is frozen at the current value. The PM must update it manually via the Project Overview.
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    <Separator />

                    {/* Invoice Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-fixed-invoice-template" className="text-base font-semibold">
                        Invoice Template
                      </Label>
                      <Select
                        value={drawerSettings.invoiceTemplateId ? drawerSettings.invoiceTemplateId.toString() : (invoiceTemplates.find(t => t.isDefault)?.id || invoiceTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("invoiceTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-fixed-invoice-template" className="w-full">
                          <SelectValue placeholder="Select invoice template" />
                        </SelectTrigger>
                        <SelectContent>
                          {invoiceTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Choose the template to use when generating invoices for this project
                      </p>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => navigate('/settings/templates')}
                      >
                        Manage Templates →
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  Billing settings are not available for this project type.
                </p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Invoice Generation Wizard */}
      <InvoiceGenerationWizard
        open={invoiceWizardOpen}
        onOpenChange={(open) => {
          setInvoiceWizardOpen(open);
          // Reset Fixed Fee milestone when wizard closes so it doesn't carry over
          if (!open) setFixedFeeMilestone(null);
        }}
        selectedWipItems={wipItems.filter((item) => selectedWipItems.includes(item.id)) as { id: string; date: string; resource: string; task: string; description: string; type: 'Time' | 'Expense' | 'Purchase'; quantity: number; unit: string; rate: number; amount: number; costCentre: string; reference: string; costRate: number; }[]}
        projectName={project?.name || ""}
        projectCode={project?.code || ""}
        client={client}
        company={currentCompany}
        expenseRecharge={projectSettings.expenseRecharge}
        expenseMarkupPercent={projectSettings.expenseMarkupPercent}
        purchaseRecharge={projectSettings.purchaseRecharge}
        purchaseMarkupPercent={projectSettings.purchaseMarkupPercent}
        notToExceedCapEnabled={projectSettings.notToExceedCapEnabled}
        notToExceedCapAmount={projectSettings.notToExceedCapAmount}
        invoiceTemplateId={projectSettings.invoiceTemplateId != null ? projectSettings.invoiceTemplateId : undefined}
        billingEvent={fixedFeeMilestone != null ? { name: fixedFeeMilestone.name, amount: fixedFeeMilestone.amount, dueDate: fixedFeeMilestone.dueDate } : undefined}
        onTemplateChange={async (templateId) => {
          // Task 2.12c — persist the template change back to the project's billing config
          setProjectSettings((prev) => ({ ...prev, invoiceTemplateId: templateId.toString() }));
          if (numericProjectId) {
            await updateProjectBillingConfig(numericProjectId, { invoiceTemplateId: templateId });
          }
        }}
        onInvoiceGenerated={async (invoice) => {
          if (dataMode !== "live") {
            console.log("Invoice generated (demo mode):", invoice);
            setSelectedWipItems([]);
            return;
          }

          try {
            setBillingActionLoading(true);

            // Extract numeric WIP IDs from wizard line items
            const wipItemIds = invoice.lineItems
              .map((li: { originalWipId?: string }) => {
                if (!li.originalWipId) return null;
                const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
                return isNaN(numId) ? null : numId;
              })
              .filter((id: number | null): id is number => id !== null);

            if (wipItemIds.length === 0) {
              console.warn("No valid WIP item IDs found in wizard line items");
              return;
            }

            // Step 1: Create draft invoice from selected WIP items
            const description = invoice.reference || undefined;
            const notes = invoice.notes || undefined;
            const invoiceDateStr = invoice.date ? new Date(invoice.date).toISOString() : undefined;
            const dueDateStr = invoice.dueDate ? new Date(invoice.dueDate).toISOString() : undefined;
            const createdInvoice = await createBillingDraft(numericProjectId, wipItemIds, description, notes, invoiceDateStr, dueDateStr);

            // Step 2: Apply any edits the user made in the wizard (quantity/rate/description changes)
            const edits: Array<{ wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string }> = [];
            for (const li of invoice.lineItems) {
              if (!li.originalWipId) continue;
              const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
              if (isNaN(numId)) continue;

              // Find the original WIP item to compare values
              const originalWip = wipItems.find((w) => w.id === li.originalWipId);
              if (!originalWip) continue;

              const edit: { wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string } = { wipLedgerId: numId };
              let hasChanges = false;

              if (li.quantity !== originalWip.quantity) {
                edit.quantity = li.quantity;
                hasChanges = true;
              }
              if (li.rate !== originalWip.rate) {
                edit.rate = li.rate;
                hasChanges = true;
              }
              if (li.amount !== originalWip.amount) {
                edit.netAmount = li.amount;
                hasChanges = true;
              }

              if (hasChanges) {
                edits.push(edit);
              }
            }

            if (edits.length > 0) {
              await updateBillingDraft(createdInvoice.id, { edits });
            }

            // Step 3: Post the draft to Access Financials to create the sales transaction
            const result = await postBillingDraftToFinancials(createdInvoice.id);

            if (result.success) {
              toast.success("Invoice generated and posted to Access Financials", {
                description: result.financialsIds?.length
                  ? `Transaction ID${result.financialsIds.length > 1 ? "s" : ""}: ${result.financialsIds.join(", ")}`
                  : undefined,
              });
              setSelectedWipItems([]);
              await refetchBillingData();
            } else if (result.validationErrors && result.validationErrors.length > 0) {
              // Validation errors from Financials — invoice stays as draft
              const errorMessages = result.validationErrors
                .map((e) => {
                  const details = e.details?.map((d) => `${d.field}: ${d.value}`).join(", ");
                  return details ? `${e.message} (${details})` : e.message;
                })
                .join("\n");
              toast.error("Financials validation failed", {
                description: errorMessages,
                duration: 10000,
              });
              // Still refetch to show the draft invoice that was created
              setSelectedWipItems([]);
              await refetchBillingData();
            } else {
              // Unexpected error — invoice stays as draft
              toast.error("Failed to post invoice to Financials", {
                description: result.error || "An unexpected error occurred. The invoice has been saved as a draft.",
                duration: 8000,
              });
              setSelectedWipItems([]);
              await refetchBillingData();
            }
          } catch (error) {
            console.error("Failed to generate invoice:", error);
            handleApiError(error, `/api/billing/${numericProjectId}/drafts`, "POST");
          } finally {
            setBillingActionLoading(false);
          }
        }}
      />

      {/* Create Draft Dialog */}
      <CreateDraftDialog
        open={createDraftDialogOpen}
        onOpenChange={setCreateDraftDialogOpen}
        selectedItemCount={selectedWipItems.length}
        onConfirm={handleCreateDraft}
        loading={billingActionLoading}
      />

      {/* Invoice Details Sheet */}
      <InvoiceDetailsSheet
        invoiceId={selectedInvoiceId}
        open={invoiceDetailsSheetOpen}
        onOpenChange={setInvoiceDetailsSheetOpen}
        onDeleteDraft={handleDeleteDraft}
        onReturnToDraft={() => refetchBillingData()}
      />

      {/* Invoice Column Configurator */}
      <ColumnConfiguratorDrawer
        open={invoiceColumnConfigOpen}
        onOpenChange={setInvoiceColumnConfigOpen}
        columns={invoiceColumns}
        onSave={(updatedColumns) => {
          updateInvoiceColumns(updatedColumns);
        }}
        defaultColumns={DEFAULT_INVOICE_COLUMNS}
      />

      {/* WIP Column Configurator */}
      <ColumnConfiguratorDrawer
        open={wipColumnConfigOpen}
        onOpenChange={setWipColumnConfigOpen}
        columns={wipColumns}
        onSave={(updatedColumns) => {
          updateWipColumns(updatedColumns);
        }}
        defaultColumns={DEFAULT_WIP_COLUMNS}
      />
    </div>
  );
}
